import { themedConfirm, themedPrompt } from '../../lib/themed-dialogs';
// Orchestrator Module — deklarativní správa celých prostředí
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mcpCall } from '../../lib/mcp';
import { useIsPhoneLayout } from '../../lib/ui-layout';
import { ModuleScopeBar } from '../../widgets/ModuleScopeBar';
import { useMeshPeers } from '../../widgets/NodePicker';
import { nodeDisplayLabel, peerMatchesKey } from '../../widgets/node-targets';
import { callWidgetTool } from '../../widgets/scope';
import { envIsLocalToListedNode, routingKeyForEnv } from './orchestrator-scope';
import { StackFlowGraphic, stackFromWizardSpec } from '../../components/StackFlowGraphic';
import { ProjectBackupCard } from '../../components/ProjectBackupCard';
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  PageHeader,
  PublishPanel,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../components/ui';
import {
  AlertCircle,
  Archive,
  CheckCircle2,
  Clock,
  Cloud,
  Cpu,
  Database,
  Download,
  Eye,
  GitBranch,
  GitMerge,
  Globe,
  HardDrive,
  History,
  Layout,
  Loader2,
  MemoryStick,
  Play,
  Plus,
  RefreshCw,
  Rocket,
  Server,
  Shield,
  Trash2,
  XCircle,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Types ─────────────────────────────────────────────────────────────────────

type EnvStatus =
  | 'pending'
  | 'running'
  | 'applied'
  | 'partial'
  | 'stopped'
  | 'error'
  | 'destroying'
  | 'destroyed'
  | 'verifying'
  | 'unknown';

interface ServiceState {
  name: string;
  module: string;
  status: string;
  instance_name?: string;
  target_node?: string;
  meta?: Record<string, unknown>;
  error?: string;
}

interface OrchestratorEnv {
  id: string;
  name: string;
  project_id?: string;
  status: EnvStatus;
  /** false = snapshot z catalogu, ne ověřený runtime (nesmí se tvářit jako pending deploy) */
  status_known?: boolean;
  /** Příznak projektu z definice: active | migrating | moving | moved | template | suspended */
  flag?: ProjectFlagState;
  flag_migration?: { target_project_id?: string; target_node?: string; phase?: string } | null;
  spec: { services?: Record<string, unknown> } & Record<string, unknown>;
  state: { services?: ServiceState[] } | Record<string, ServiceState>;
  created_at: string;
  updated_at: string;
  primary_node?: string | null;
  target_node?: string | null;
  is_local?: boolean;
  owner_node?: string | null;
  owner_node_id?: string | null;
  has_local_state?: boolean;
  services?: number;
  running?: string;
}

interface EnvEvent {
  id: string;
  service: string | null;
  action: string;
  status: string;
  message: string;
  created_at: string;
}

interface OrchestratorEnvStatus extends OrchestratorEnv {
  events?: EnvEvent[];
}

// ── Status helpers ────────────────────────────────────────────────────────────

const STATUS_CFG: Record<EnvStatus, { cls: string; Icon: typeof CheckCircle2; labelKey: string }> =
  {
    pending: {
      cls: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
      Icon: Clock,
      labelKey: 'statusPending',
    },
    applied: {
      cls: 'text-green-400 bg-green-500/10 border-green-500/30',
      Icon: CheckCircle2,
      labelKey: 'statusApplied',
    },
    running: {
      cls: 'text-green-400 bg-green-500/10 border-green-500/30',
      Icon: CheckCircle2,
      labelKey: 'statusRunning',
    },
    partial: {
      cls: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
      Icon: AlertCircle,
      labelKey: 'statusPartial',
    },
    stopped: {
      cls: 'text-gray-400 bg-gray-800 border-gray-700',
      Icon: Clock,
      labelKey: 'statusStopped',
    },
    error: {
      cls: 'text-red-400 bg-red-500/10 border-red-500/30',
      Icon: XCircle,
      labelKey: 'statusError',
    },
    destroying: {
      cls: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
      Icon: Loader2,
      labelKey: 'statusDestroying',
    },
    destroyed: {
      cls: 'text-gray-600 bg-gray-900 border-gray-800',
      Icon: XCircle,
      labelKey: 'statusDestroyed',
    },
    // Ověřování / neznámý snapshot — NIKDY label "Čeká na deploy"
    verifying: {
      cls: 'text-blue-300 bg-blue-500/10 border-blue-500/30',
      Icon: Loader2,
      labelKey: 'statusVerifying',
    },
    unknown: {
      cls: 'text-gray-400 bg-gray-800/80 border-gray-700',
      Icon: Clock,
      labelKey: 'statusUnknown',
    },
  };

type ProjectFlagState = 'active' | 'migrating' | 'moving' | 'moved' | 'template' | 'suspended';

const FLAG_CFG: Record<Exclude<ProjectFlagState, 'active'>, { cls: string; labelKey: string }> = {
  migrating: { cls: 'text-sky-300 bg-sky-500/10 border-sky-500/30', labelKey: 'flagMigrating' },
  moving: { cls: 'text-amber-300 bg-amber-500/10 border-amber-500/30', labelKey: 'flagMoving' },
  moved: { cls: 'text-gray-300 bg-gray-700/60 border-gray-500/40', labelKey: 'flagMoved' },
  template: {
    cls: 'text-violet-300 bg-violet-500/10 border-violet-500/30',
    labelKey: 'flagTemplate',
  },
  suspended: { cls: 'text-gray-400 bg-gray-800 border-gray-700', labelKey: 'flagSuspended' },
};

/** Příznak projektu z definice. Přenesený zdroj ukáže i kam provoz odešel. */
function FlagBadge(
  { flag, migration }: {
    flag?: ProjectFlagState;
    migration?: { target_project_id?: string; target_node?: string; phase?: string } | null;
  },
) {
  const { t } = useTranslation();
  if (!flag || flag === 'active') return null;
  const cfg = FLAG_CFG[flag];
  const where = flag === 'moved' && (migration?.target_node || migration?.target_project_id)
    ? ` → ${migration?.target_node ?? ''}${
      migration?.target_project_id ? ` ${migration.target_project_id.slice(0, 8)}…` : ''
    }`
    : migration?.phase
    ? `: ${migration.phase}`
    : '';
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-medium ${cfg.cls}`}
      title={migration?.phase}
    >
      {t(`orchestrator.${cfg.labelKey}`)}
      {where}
    </span>
  );
}

function StatusBadge({ status }: { status: EnvStatus }) {
  const { t } = useTranslation();
  const cfg = STATUS_CFG[status] ?? STATUS_CFG.unknown;
  const spin = status === 'verifying';
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-medium ${cfg.cls}`}
    >
      <cfg.Icon size={10} className={spin ? 'animate-spin' : undefined} />
      {t(`orchestrator.${cfg.labelKey}`)}
    </span>
  );
}

const OrchNodeCtx = createContext<string | null>(null);

function useOrchCall() {
  const node = useContext(OrchNodeCtx);
  return useCallback(
    <T,>(
      tool: string,
      args: Record<string, unknown> = {},
      opts?: { silent?: boolean; label?: string },
    ) => callWidgetTool<T>(tool, args, { node, identity: null }, opts),
    [node],
  );
}

const SVC_ICONS: Record<string, typeof Database> = {
  dbhost: Database,
  apphost: Server,
  supabase: Database,
  npm: Globe,
  dns: Globe,
  backup: Archive,
  'data-proxy': Zap,
  external: Cloud,
};

const SVC_COLORS: Record<string, string> = {
  dbhost: 'text-blue-400',
  apphost: 'text-green-400',
  supabase: 'text-purple-400',
  npm: 'text-amber-400',
  dns: 'text-cyan-400',
  backup: 'text-gray-400',
  external: 'text-sky-400',
};

// ── Environment Card ──────────────────────────────────────────────────────────

function EnvCard({ env, onSelect, selected, onLiveStatus, showNodeBadge = true }: {
  env: OrchestratorEnv;
  onSelect: () => void;
  selected: boolean;
  onLiveStatus?: (name: string, status: EnvStatus) => void;
  showNodeBadge?: boolean;
}) {
  const { t } = useTranslation();
  const scopeNode = useContext(OrchNodeCtx);
  const mcpCall = useOrchCall();
  const isRemote = !!scopeNode || env.is_local === false;
  const nodeKey = env.primary_node ?? env.target_node ?? (isRemote ? 'unknown' : 'local');

  // Live probe na vybraném nodu (Lokál = tady, jinak mesh.rpc_call).
  // Dokud probe nedoběhne: badge "Ověřuji…", NIKDY "Čeká na deploy".
  const { data: detail, isLoading: detailLoading, isError: detailError } = useQuery({
    queryKey: ['orch-card', env.name, scopeNode ?? 'local'],
    queryFn: () =>
      mcpCall<{
        ok: boolean;
        status?: string;
        project_id?: string;
        name?: string;
        primary_node?: string;
        is_local?: boolean;
        services?: Record<
          string,
          { module: string; status: string; instance: string; meta?: Record<string, unknown> }
        >;
      }>('orchestrator.orch_status', { name: env.name }, { silent: true }),
    staleTime: 15_000,
    refetchInterval: 20_000,
  });

  const verified = !detailLoading && !!detail?.status;
  // Snapshot pending z catalogu u remote bez local state = neznámý, ne "čeká deploy"
  const snapshotTrusted = env.status_known !== false &&
    !(isRemote && env.has_local_state === false && env.status === 'pending');

  const status: EnvStatus = detailError
    ? 'error'
    : !verified
    ? 'verifying'
    : (detail!.status as EnvStatus) ??
      (snapshotTrusted ? env.status : 'unknown');

  useEffect(() => {
    if (detailError) {
      onLiveStatus?.(env.name, 'error');
      return;
    }
    if (verified && detail?.status) {
      onLiveStatus?.(env.name, detail.status as EnvStatus);
    }
  }, [detailError, verified, detail?.status, env.name, onLiveStatus]);

  const projectId = detail?.project_id ?? env.project_id;
  const svcEntries = detail?.services
    ? Object.entries(detail.services).sort(([, a], [, b]) =>
      (MODULE_DISPLAY_ORDER[a.module] ?? 9) - (MODULE_DISPLAY_ORDER[b.module] ?? 9)
    )
    : [];
  const svcCount = svcEntries.length || env.services || 0;
  const runCount = svcEntries.filter(([, s]) => s.status === 'running').length;
  const isApplied = status === 'applied' || status === 'running';

  return (
    <div
      onClick={onSelect}
      className={`cursor-pointer border-2 rounded-xl p-3 sm:p-4 transition-all touch-manipulation ${
        selected
          ? 'border-blue-500/50 bg-blue-950/10'
          : isApplied
          ? 'border-green-500/20 bg-gray-900/20 hover:border-green-500/40'
          : isRemote
          ? 'border-violet-500/20 bg-violet-950/5 hover:border-violet-500/40'
          : 'border-gray-800 bg-gray-900/20 hover:border-gray-600'
      }`}
    >
      <div className='flex items-center justify-between mb-2 gap-2'>
        <div className='flex items-center gap-2 min-w-0'>
          <div
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              status === 'verifying'
                ? 'bg-blue-400 animate-pulse'
                : isApplied
                ? 'bg-green-500'
                : status === 'error' || status === 'partial'
                ? 'bg-orange-500'
                : isRemote
                ? 'bg-violet-500'
                : 'bg-gray-600'
            }`}
          />
          <span className='text-sm font-bold text-white truncate'>{env.name}</span>
        </div>
        <StatusBadge status={status} />
      </div>

      <div className='flex flex-wrap items-center gap-1.5 mb-2'>
        {projectId && (
          <span className='font-mono text-[10px] bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded'>
            {projectId}
          </span>
        )}
        {showNodeBadge && nodeKey && (
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${
              env.is_local !== false && (nodeKey === 'local' || env.is_local)
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
                : 'bg-violet-500/10 text-violet-300 border-violet-500/25'
            }`}
            title={nodeKey}
          >
            <Server size={9} />
            {nodeKey === 'local'
              ? t('orchestrator.thisServer', 'Tento server')
              : ((env as { primary_node_label?: string }).primary_node_label ||
                env.owner_node ||
                (/^[0-9a-f-]{36}$/i.test(nodeKey) ? `${nodeKey.slice(0, 8)}…` : nodeKey))}
          </span>
        )}
      </div>

      {detailLoading && (
        <div className='flex items-center gap-2 text-[11px] text-gray-500 py-1'>
          <span className='inline-block w-3 h-3 border-2 border-gray-600 border-t-blue-400 rounded-full animate-spin' />
          {isRemote
            ? t('orchestrator.verifyingRemote', 'Ověřuji stav na {{node}}…', { node: nodeKey })
            : t('orchestrator.verifyingTopology', 'Načítám živý stav a ověřuji topologii…')}
        </div>
      )}
      {detailError && !detailLoading && (
        <div className='text-[11px] text-amber-400/90 py-1'>
          {t('orchestrator.statusProbeFailed', 'Stav se nepodařilo ověřit')}
        </div>
      )}
      {svcEntries.length > 0
        ? (
          <div
            className={`rounded-lg border p-2 mt-1 ${
              isApplied ? 'border-green-500/10 bg-green-500/5' : 'border-gray-800 bg-gray-900/30'
            }`}
          >
            <div className='flex flex-wrap gap-1'>
              {svcEntries.map(([svcName, svc]) => {
                const Icon = SVC_ICONS[svc.module] ?? Server;
                const ok = svc.status === 'running';
                return (
                  <div
                    key={svcName}
                    className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-md ${
                      ok ? 'bg-green-500/10 text-green-400' : 'bg-gray-800/50 text-gray-500'
                    }`}
                  >
                    <Icon size={9} />
                    <span className='font-medium'>{svcName}</span>
                  </div>
                );
              })}
            </div>
            <div className='mt-2 flex items-center gap-2'>
              <div className='flex-1 h-1 rounded-full bg-gray-800 overflow-hidden'>
                <div
                  className={`h-full rounded-full transition-all ${
                    runCount === svcEntries.length ? 'bg-emerald-500' : 'bg-amber-500'
                  }`}
                  style={{ width: `${Math.round((runCount / svcEntries.length) * 100)}%` }}
                />
              </div>
              <span className='text-[10px] text-gray-500 tabular-nums shrink-0'>
                {runCount}/{svcEntries.length}
              </span>
            </div>
          </div>
        )
        : (
          !detailLoading && (
            <div className='text-[10px] text-gray-600 mt-1'>
              {svcCount} {t('orchestrator.services')}
              {env.running && snapshotTrusted ? ` · ${env.running}` : ''}
              {isRemote ? ` · @${nodeKey}` : ''}
            </div>
          )
        )}
    </div>
  );
}

// ── Environment Detail ────────────────────────────────────────────────────────

function EnvDetail({
  envId,
  primaryNode,
  isLocal,
  onDeleted,
}: {
  envId: string;
  primaryNode?: string | null;
  isLocal?: boolean;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const scopeNode = useContext(OrchNodeCtx);
  const mcpCall = useOrchCall();
  const { data: meshPeers } = useMeshPeers();
  const scopePeer = scopeNode
    ? (meshPeers ?? []).find((p) => peerMatchesKey(p, scopeNode))
    : undefined;
  const scopeLabel = scopePeer ? nodeDisplayLabel(scopePeer) : (primaryNode || scopeNode || null);

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['orch-status', envId, scopeNode ?? 'local'],
    queryFn: () =>
      mcpCall<{ ok: boolean } & OrchestratorEnvStatus>(
        'orchestrator.orch_status',
        { name: envId },
      ),
    refetchInterval: 15_000,
  });

  if (isLoading) {
    return (
      <div className='py-12 text-center text-gray-500'>
        <Loader2 size={16} className='animate-spin inline mr-2' />
        {scopeNode
          ? t('orchestrator.verifyingRemote', 'Ověřuji stav na {{node}}…', {
            node: scopeLabel || scopeNode,
          })
          : t('orchestrator.loading')}
      </div>
    );
  }
  if (isError || !data?.ok) {
    return (
      <div className='py-10 text-center text-sm text-gray-500'>
        {t('orchestrator.statusLoadFailed', 'Stav prostředí se nepodařilo načíst.')}
        {(error as Error | undefined)?.message && (
          <div className='mt-2 text-[11px] text-gray-600 font-mono'>
            {(error as Error).message}
          </div>
        )}
      </div>
    );
  }

  // orch_status na remote nodu vrací primary=local z POHLEDU cíle.
  // Z acm-dev by se to tvářilo jako "tento server". Bereme picker.
  const viewingRemote = !!scopeNode ||
    (isLocal === false && !!primaryNode && primaryNode !== 'local' &&
      primaryNode !== 'unknown' && !String(primaryNode).startsWith('node:'));
  const viewPrimary = viewingRemote
    ? (scopeLabel || scopeNode || primaryNode || 'remote')
    : ((data as OrchestratorEnv).primary_node ??
      (data as OrchestratorEnv).target_node ??
      primaryNode ??
      'local');
  const viewIsLocal = viewingRemote ? false : isLocal !== false;

  const viewEnv = {
    ...data,
    primary_node: viewPrimary,
    target_node: viewPrimary,
    is_local: viewIsLocal,
    // Služby bez target_node na remote = běží na primaryNode, ne na "local" UI nodu
    services: (() => {
      const raw =
        (data as { services?: Record<string, { target_node?: string; [k: string]: unknown }> })
          .services;
      if (!raw || !viewingRemote) return raw;
      const out: typeof raw = {};
      for (const [k, svc] of Object.entries(raw)) {
        const tn = svc?.target_node;
        out[k] = {
          ...svc,
          target_node: (!tn || tn === 'local') ? viewPrimary : tn,
        };
      }
      return out;
    })(),
  } as typeof data & OrchestratorEnv;

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2 sm:gap-3 flex-wrap'>
        <h2 className='text-base sm:text-lg font-bold text-white truncate max-w-[70vw] sm:max-w-none'>
          {data.name}
        </h2>
        {viewPrimary && (
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${
              viewIsLocal
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
                : 'bg-violet-500/10 text-violet-300 border-violet-500/25'
            }`}
          >
            <Server size={10} />
            {viewIsLocal && viewPrimary === 'local'
              ? t('orchestrator.thisServer', 'Tento server')
              : viewPrimary === 'local'
              ? t('orchestrator.thisServer', 'Tento server')
              : viewPrimary}
          </span>
        )}
        <StatusBadge status={data.status as EnvStatus} />
        <span className='text-xs text-gray-600 font-mono'>{data.id}</span>
        {
          /* isFetching (ne jen isLoading) — background refetchInterval poll jinak
            neměl žádný vizuální projev, takže "za běhu" vypadalo jako by se
            nic nedělo (Filip 2026-07-27). isLoading pokrývá jen první fetch. */
        }
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          title={isFetching ? t('orchestrator.refreshing', 'Aktualizuji…') : undefined}
          className='ml-auto p-1.5 text-gray-600 hover:text-white transition-colors disabled:cursor-wait'
        >
          <RefreshCw size={13} className={isFetching ? 'animate-spin' : undefined} />
        </button>
      </div>

      <Tabs defaultTab='status'>
        <TabList>
          <Tab id='status'>{t('orchestrator.tabStatus')}</Tab>
          <Tab id='release'>{t('orchestrator.tabOperations')}</Tab>
          <Tab id='events'>{t('orchestrator.tabEvents')}</Tab>
          <Tab id='manage'>{t('orchestrator.tabManage')}</Tab>
        </TabList>

        <TabPanel id='status'>
          <StatusPanel
            env={viewEnv}
            onChanged={() => {
              qc.invalidateQueries({ queryKey: ['orch-status', envId] });
              qc.invalidateQueries({ queryKey: ['orch-list'] });
            }}
          />
        </TabPanel>
        <TabPanel id='release'>
          <ProvozPanel
            env={viewEnv}
            onChanged={() => {
              qc.invalidateQueries({ queryKey: ['orch-status', envId] });
              qc.invalidateQueries({ queryKey: ['orch-list'] });
            }}
          />
        </TabPanel>
        <TabPanel id='events'>
          <EventLogPanel events={data.events ?? []} />
        </TabPanel>
        <TabPanel id='manage'>
          <div className='space-y-4'>
            <NastaveniPanel
              env={viewEnv}
              onChanged={() => {
                qc.invalidateQueries({ queryKey: ['orch-status', envId] });
                qc.invalidateQueries({ queryKey: ['orch-list'] });
              }}
            />
            <ManagePanel env={viewEnv} onDeleted={onDeleted} />
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
}

// ── Tech config for visual ───────────────────────────────────────────────────

const TECH_CFG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  supabase: {
    label: 'Supabase',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
  },
  apphost: {
    label: 'AppHost',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
  },
  dbhost: {
    label: 'DBHost',
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/30',
  },
  npm: {
    label: 'Proxy',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
  },
  dns: { label: 'DNS', color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/30' },
  backup: {
    label: 'Backup',
    color: 'text-gray-400',
    bg: 'bg-gray-500/10',
    border: 'border-gray-500/30',
  },
  external: {
    label: 'External',
    color: 'text-sky-400',
    bg: 'bg-sky-500/10',
    border: 'border-sky-500/30',
  },
};

// Mapování provider klíče (z service meta.provider) na display label pro badge.
// Rozšiř podle reality (heroku, supabase-cloud, atd.).
const EXTERNAL_PROVIDER_LABEL: Record<string, string> = {
  'lovable-cloud': 'Lovable Cloud',
  'supabase-cloud': 'Supabase Cloud',
  'cloudflare': 'Cloudflare',
  'aws': 'AWS',
  'azure': 'Azure',
  'gcp': 'GCP',
};

const STATUS_LABEL: Record<string, string> = {
  running: 'svcStatusRunning',
  syncing: 'svcStatusSyncing',
  stopped: 'svcStatusStopped',
  error: 'svcStatusError',
  unknown: 'svcStatusUnknown',
};

// Display order: internet → app → DB (od uživatele po data)
const MODULE_DISPLAY_ORDER: Record<string, number> = {
  dns: 0,
  npm: 1,
  apphost: 2,
  supabase: 3,
  dbhost: 4,
  'data-proxy': 5,
  backup: 6,
};

// ── Status Panel — Visual Server Layout ──────────────────────────────────────

function StatusPanel({ env, onChanged }: {
  env: OrchestratorEnvStatus;
  onChanged?: () => void;
}) {
  const mcpCall = useOrchCall();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [destroying, setDestroying] = useState<string | null>(null);
  const [deploying, setDeploying] = useState<string | null>(null);

  const { data: meshPeers } = useMeshPeers({ includeLocal: true });
  const nodeLabel = (nodeId: string): string => {
    if (!nodeId || nodeId === 'local') {
      const local = (meshPeers ?? []).find((p) => p.is_local);
      return local ? nodeDisplayLabel(local) : t('orchestrator.thisServer');
    }
    const found = (meshPeers ?? []).find((p) => peerMatchesKey(p, nodeId));
    if (found) return nodeDisplayLabel(found);
    const fromEnv = env as { primary_node_label?: string; owner_node?: string | null };
    if (fromEnv.primary_node_label && fromEnv.primary_node_label !== nodeId) {
      return fromEnv.primary_node_label;
    }
    if (fromEnv.owner_node && fromEnv.owner_node !== nodeId) return fromEnv.owner_node;
    return nodeId;
  };
  const moduleHref = (mod: string, instance: string) => {
    return `/modules/${mod}?instance=${encodeURIComponent(instance)}`;
  };

  // Active backend pointer — který service key z spec.services má FE
  // momentálně napojený. Multi-backend pattern: env může mít 2+ BE služeb
  // (např. cloud Lovable + on-prem Supabase) a jeden je "aktivní" (= FE
  // client.ts URL ukazuje sem), ostatní jsou "záložní" (= existují, ale
  // FE je nepoužívá). Bez explicitního active_backend padáme na heuristiku
  // (cascade match z předchozí topology logiky).
  const activeBackend = (env.spec as unknown as { active_backend?: string })
    ?.active_backend ?? undefined;
  const isBackendModule = (mod: string) =>
    mod === 'external' || mod === 'supabase' || mod === 'dbhost';

  // ── DVA ZDROJE PRAVDY ────────────────────────────────────────────────────────
  // env.spec.services = MÁ BÝT (deklarativní šablona)
  // env.services      = JE (live enrichment z orch_status — read-only)
  //
  // UI vykreslí UNION (spec ∪ live):
  //   - service v obou + status=running   → vybarvený slot (OK)
  //   - service v obou + status=missing   → oranžový (smazána mimo orch)
  //   - service jen v spec (žádný live)   → ghost placeholder (neaplikováno)
  //   - service jen v live (žádný spec)   → orphan badge (orch o tom neví)

  const liveMap = (env as unknown as {
    services?: Record<string, {
      module: string;
      status: string;
      instance: string;
      target_node?: string;
      meta?: Record<string, unknown>;
    }>;
  }).services ?? {};
  const specMap = ((env.spec as unknown as {
    services?: Record<string, { module?: string; target_node?: string; [k: string]: unknown }>;
  }).services) ?? {};

  // Union — pro každou unikátní svcName z obou zdrojů
  const allNames = new Set([...Object.keys(specMap), ...Object.keys(liveMap)]);
  const svcEntries = Array.from(allNames).map((svcName) => {
    const spec = specMap[svcName];
    const live = liveMap[svcName];
    const module = (spec?.module ?? live?.module ?? 'unknown') as string;
    let driftState: 'running' | 'missing' | 'spec_only' | 'orphan' | 'other';
    if (!live && spec) driftState = 'spec_only';
    else if (live && !spec) driftState = 'orphan';
    else if (live?.status === 'missing') driftState = 'missing';
    else if (live?.status === 'running') driftState = 'running';
    else driftState = 'other';

    // Synthesize live entry for spec-only (uniform render)
    const synthesized = live ?? {
      module,
      status: 'spec_only',
      instance: '—',
      target_node: spec?.target_node,
      meta: { spec_definition: spec },
    };
    return [svcName, synthesized, driftState] as const;
  }).sort((a, b) => {
    // Sekundární klíč: aktivní backend nahoru v rámci same-module skupiny.
    // Bez tohoto musí Filip vědět který je aktivní z badge; teď ho vidí
    // graficky první v seznamu (= odpovídá flow chainu nahoře).
    const modA = MODULE_DISPLAY_ORDER[a[1].module] ?? 9;
    const modB = MODULE_DISPLAY_ORDER[b[1].module] ?? 9;
    if (modA !== modB) return modA - modB;
    const activeBe = (env.spec as unknown as { active_backend?: string })?.active_backend;
    if (activeBe) {
      if (a[0] === activeBe && b[0] !== activeBe) return -1;
      if (b[0] === activeBe && a[0] !== activeBe) return 1;
    }
    return a[0].localeCompare(b[0]);
  });

  const envPrimary = (env as OrchestratorEnv).primary_node ??
    (env as OrchestratorEnv).target_node ??
    null;
  const envIsLocal = (env as OrchestratorEnv).is_local !== false &&
    (!envPrimary || envPrimary === 'local');
  // Remote env (mc01): chybějící/local target_node služby = ten remote nod,
  // NIKDY "local" = acm-dev v UI.
  const defaultNodeKey = !envIsLocal
    ? (envPrimary && envPrimary !== 'local' ? envPrimary : 'unknown')
    : 'local';

  // Group by node — null ≠ local pro cizí mesh env
  const nodeGroups = new Map<string, typeof svcEntries>();
  for (const entry of svcEntries) {
    const svc = entry[1];
    let node: string;
    if (svc.module === 'external') {
      const provider = (svc.meta?.provider as string | undefined) ?? 'cloud';
      node = `cloud:${provider}`;
    } else {
      const raw = svc.target_node ?? defaultNodeKey;
      // "local" z remote orch_status = primární nod envu, ne tento UI server
      node = (!envIsLocal && (raw === 'local' || !raw)) ? defaultNodeKey : raw;
    }
    if (!nodeGroups.has(node)) nodeGroups.set(node, []);
    nodeGroups.get(node)!.push(entry);
  }
  const sortedNodeEntries = [...nodeGroups.entries()].sort(([a], [b]) => {
    const aLocal = a === 'local';
    const bLocal = b === 'local';
    if (aLocal !== bLocal) return aLocal ? -1 : 1;
    const aCloud = a.startsWith('cloud:');
    const bCloud = b.startsWith('cloud:');
    if (aCloud !== bCloud) return aCloud ? 1 : -1;
    return a.localeCompare(b);
  });

  // Find project_id from any live service meta
  const projectId = env.project_id ??
    (svcEntries.find(([, s]) => s.meta?.project_id)?.[1]?.meta?.project_id as
      | string
      | undefined);
  const runCount = svcEntries.filter(([, , drift]) => drift === 'running').length;
  const driftCount =
    svcEntries.filter(([, , drift]) =>
      drift === 'missing' || drift === 'spec_only' || drift === 'orphan'
    ).length;

  // Per-service destroy
  const destroyService = async (svcName: string) => {
    if (
      !(await themedConfirm(
        t('orchestrator.confirmRemoveService', { service: svcName, env: env.name }),
      ))
    ) return;
    setDestroying(svcName);
    try {
      const r = await mcpCall<{ ok: boolean; detail?: string; error?: string }>(
        'orchestrator.orch_destroy_service',
        { name: env.name, service: svcName, delete_data: false, keep_in_spec: false },
      );
      if (r.ok) {
        toast.success(t('orchestrator.serviceRemoved', { service: svcName }));
        onChanged?.();
      } else toast.error(r.error ?? t('orchestrator.destroyFailed'));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setDestroying(null);
    }
  };

  // Update apphost z gitu — preferuje lovable.refresh_build (= git pull + npm build
  // + dist swap + restart bez ztráty client.ts override), pro non-lovable
  // apphost padá na apphost.deploy (git clone/pull + composer/npm + rebuild).
  const deployService = async (svcName: string, instance: string, isLovable: boolean) => {
    setDeploying(svcName);
    try {
      if (isLovable) {
        const r = await mcpCall<{
          ok: boolean;
          error?: string;
          head_commit?: string;
          duration_ms?: number;
        }>('apphost.lovable.refresh_build', { apphost_name: instance });
        if (r.ok) {
          toast.success(
            t('orchestrator.buildOk', {
              head: r.head_commit ?? '?',
              seconds: Math.round((r.duration_ms ?? 0) / 1000),
            }),
          );
          onChanged?.();
        } else toast.error(r.error ?? t('orchestrator.buildFailed'));
      } else {
        const r = await mcpCall<{ ok: boolean; error?: string }>('apphost.deploy', {
          name: instance,
        });
        if (r.ok) {
          toast.success(t('orchestrator.deployOk', { instance }));
          onChanged?.();
        } else toast.error(r.error ?? t('orchestrator.deployFailed'));
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setDeploying(null);
    }
  };

  // Swap backend — přepne FE na jiný backend service.
  // Volá orch_swap_backend tool: rewrite .env (VITE_SUPABASE_URL +
  // _PUBLISHABLE_KEY) + npm build + restart apphost container + update
  // spec.active_backend. Vyžaduje konfirmaci v UI před voláním
  // (= destruktivní, ~30-60s downtime, target backend musí mít data).
  const [swapping, setSwapping] = useState<string | null>(null);
  const swapBackend = async (targetBackend: string, anonKeyOverride?: string) => {
    setSwapping(targetBackend);
    try {
      const r = await mcpCall<{
        ok: boolean;
        error?: string;
        previous_backend?: string;
        new_backend?: string;
        new_url?: string;
        url_source?: string;
        duration_ms?: number;
        build_log?: string[];
      }>('orchestrator.orch_swap_backend', {
        env_name: env.name,
        target_backend: targetBackend,
        ...(anonKeyOverride && { anon_key_override: anonKeyOverride }),
      });
      if (r.ok) {
        toast.success(
          t('orchestrator.backendSwitchedDetail', {
            from: r.previous_backend ?? '—',
            to: r.new_backend,
            seconds: Math.round((r.duration_ms ?? 0) / 1000),
            url: r.url_source ?? '?',
          }),
        );
        onChanged?.();
      } else {
        toast.error(r.error ?? t('orchestrator.swapFailed'));
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSwapping(null);
    }
  };

  // Connect supabase backend k apphost docker network — viz
  // orch_connect_internal_backend. Bez tohoto nemá FE z svého containeru
  // přístup k internal-only supabase (= jiná network namespace).
  const [connecting, setConnecting] = useState<string | null>(null);
  const connectInternalBackend = async (backendService: string) => {
    setConnecting(backendService);
    try {
      const r = await mcpCall<{
        ok: boolean;
        error?: string;
        internal_url?: string;
        connected?: string[];
        already_connected?: string[];
        errors?: string[];
      }>('orchestrator.orch_connect_internal_backend', {
        env_name: env.name,
        backend_service: backendService,
      });
      if (r.ok) {
        const newly = (r.connected ?? []).length;
        const existing = (r.already_connected ?? []).length;
        toast.success(
          t('orchestrator.connectedDetail', {
            newly,
            existing,
            url: r.internal_url,
          }),
        );
        onChanged?.();
      } else {
        toast.error(r.error ?? (r.errors ?? []).join('; ') ?? t('orchestrator.connectFailed'));
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setConnecting(null);
    }
  };

  // ── Health / drift summary ────────────────────────────────────────────────
  // Rozdíl mezi "má být" a "je" jako CHYBY (ne ruční plán). Služby + FE↔BE
  // jsou automatické (read-only), BE drift je on-demand (volá cloud).
  const healthServices = Object.entries(liveMap).map(([name, s]) => ({
    name,
    status: s.status,
  }));
  const badServices = healthServices.filter(
    (s) => s.status !== 'running' && s.status !== 'external',
  );

  // FE↔BE kompatibilita z posledního deploye.
  const { data: feBeData } = useQuery({
    queryKey: ['orch-fe-be-compat', env.name],
    queryFn: () =>
      mcpCall<
        {
          ok: boolean;
          deploys?: Array<{ compatible?: boolean; fe_commit?: string; be_commit?: string }>;
        }
      >(
        'orchestrator.orch_deploy_history',
        { env_name: env.name, limit: 1 },
      ),
    refetchInterval: 30_000,
  });
  const lastDeploy = feBeData?.deploys?.[0];

  // Srovnání schématu — za KAŽDÝ supabase backend zvlášť. Projekt jich může
  // mít víc (`backend`, `backend-dev`) a odpověď „sedí to?" má smysl u obou.
  // Metodu vybere `supabase.schema_compare` podle toho, co zdroj pustil ven,
  // takže tlačítko funguje i bez spojení na cloud (pak se srovnává proti repu).
  const supabaseBackends = Object.keys(specMap).filter((k) => specMap[k]?.module === 'supabase');
  const apphostKey = Object.keys(specMap).find((k) => specMap[k]?.module === 'apphost');
  const sourceConnection = (env.spec as unknown as { source_connection?: string })
    .source_connection;
  const compareSourceDir = apphostKey
    ? `/opt/apphost/${env.name}-${apphostKey}/src/supabase`
    : undefined;
  interface SchemaCompare {
    ok?: boolean;
    method?: 'full' | 'live' | 'repo';
    method_reason?: string;
    identical?: boolean;
    missing_here?: string[];
    extra_here?: string[];
    changed_definition_lines?: number;
    error?: string;
  }
  const [compareBusy, setCompareBusy] = useState<string | null>(null);
  const [compareResults, setCompareResults] = useState<Record<string, SchemaCompare>>({});
  const compareSchema = async (serviceKey: string) => {
    const instance = `${env.name}-${serviceKey}`;
    setCompareBusy(serviceKey);
    try {
      const r = await mcpCall<SchemaCompare>('supabase.schema_compare', {
        instance,
        ...(sourceConnection ? { connection_id: sourceConnection } : {}),
        ...(compareSourceDir ? { source_dir: compareSourceDir } : {}),
      });
      setCompareResults((prev) => ({ ...prev, [serviceKey]: r }));
      if (r.ok === false && r.error) toast.error(r.error);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setCompareBusy(null);
    }
  };

  const hasHealthError = badServices.length > 0 || lastDeploy?.compatible === false;

  if (svcEntries.length === 0) {
    return (
      <div className='py-12 text-center text-gray-600 text-sm'>
        {t('orchestrator.envNotStarted')}
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* ── Stav (health / drift summary) ──────────────────────────────────── */}
      <Card className={hasHealthError ? 'border-red-500/40' : 'border-green-500/30'}>
        <CardContent className='p-4'>
          <div
            className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
              hasHealthError ? 'text-red-400' : 'text-green-400'
            }`}
          >
            {t('orchestrator.health')}
          </div>
          <div className='space-y-1.5'>
            {/* Služby */}
            {badServices.length > 0
              ? badServices.map((s) => (
                <div
                  key={s.name}
                  className='flex items-center gap-2 text-[11px] text-red-300'
                >
                  <XCircle size={12} className='flex-shrink-0' />
                  <span>
                    {t('orchestrator.service')} <span className='font-mono'>{s.name}</span>:{' '}
                    {s.status}
                  </span>
                </div>
              ))
              : (
                <div className='flex items-center gap-2 text-[11px] text-green-300'>
                  <CheckCircle2 size={12} className='flex-shrink-0' />{' '}
                  {t('orchestrator.servicesRunning')}
                </div>
              )}

            {/* FE↔BE */}
            {lastDeploy === undefined
              ? (
                <div className='flex items-center gap-2 text-[11px] text-gray-500'>
                  <Clock size={12} className='flex-shrink-0' /> FE↔BE —
                </div>
              )
              : lastDeploy.compatible === false
              ? (
                <div className='flex items-center gap-2 text-[11px] text-red-300'>
                  <AlertCircle size={12} className='flex-shrink-0' />
                  <span>
                    {t('orchestrator.feBeMismatch', {
                      fe: (lastDeploy.fe_commit ?? '????????').slice(0, 8),
                      be: (lastDeploy.be_commit ?? '????????').slice(0, 8),
                    })}
                  </span>
                </div>
              )
              : (
                <div className='flex items-center gap-2 text-[11px] text-green-300'>
                  <CheckCircle2 size={12} className='flex-shrink-0' /> {t('orchestrator.feBeOk')}
                </div>
              )}

            {/* Srovnání schématu — per backend, ne jeden na projekt. */}
            {supabaseBackends.map((serviceKey) => {
              const res = compareResults[serviceKey];
              const busy = compareBusy === serviceKey;
              return (
                <div key={serviceKey} className='flex items-center gap-2 flex-wrap pt-1'>
                  <button
                    onClick={() => void compareSchema(serviceKey)}
                    disabled={busy}
                    title={t('orchestrator.schemaCompareTitle')}
                    className='flex items-center gap-1.5 px-2.5 py-1 rounded border border-gray-700 bg-gray-800 text-[11px] text-gray-300 hover:bg-gray-700 disabled:opacity-40'
                  >
                    {busy ? <Loader2 size={11} className='animate-spin' /> : <Database size={11} />}
                    {t('orchestrator.compareSchema')}
                    <span className='font-mono text-gray-500'>{serviceKey}</span>
                  </button>
                  {res?.ok === false && (
                    <span className='text-[11px] text-red-300'>{res.error}</span>
                  )}
                  {res?.ok !== false && res?.identical === true && (
                    <span className='flex items-center gap-1.5 text-[11px] text-green-300'>
                      <CheckCircle2 size={12} className='flex-shrink-0' />
                      {t('orchestrator.schemaIdentical')}
                      <span className='text-gray-500'>({res.method_reason})</span>
                    </span>
                  )}
                  {res?.ok !== false && res?.identical === false && (
                    <div className='flex items-start gap-2 text-[11px] text-amber-300'>
                      <AlertCircle size={12} className='flex-shrink-0 mt-0.5' />
                      <span>
                        {t('orchestrator.schemaDiffers', {
                          count: res.missing_here?.length ?? 0,
                        })}{' '}
                        <span className='font-mono'>
                          {(res.missing_here ?? []).slice(0, 6).join(', ')}
                          {(res.missing_here ?? []).length > 6 && ' …'}
                        </span>
                        {!!res.changed_definition_lines && (
                          <span className='ml-1'>
                            · {t('orchestrator.schemaChangedLines', {
                              count: res.changed_definition_lines,
                            })}
                          </span>
                        )}
                        <span className='block text-gray-500'>{res.method_reason}</span>
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Project header */}
      <div className='flex items-center gap-3 flex-wrap'>
        {projectId && (
          <span className='font-mono text-xs bg-blue-500/10 text-blue-400 px-2.5 py-1 rounded-lg border border-blue-500/20 font-bold'>
            {projectId}
          </span>
        )}
        <span className='text-xs text-gray-500'>
          {runCount}/{svcEntries.length} {t('orchestrator.servicesRunningCount')}
        </span>
        {driftCount > 0 && (
          <span className='text-xs px-2 py-0.5 rounded-lg border border-orange-500/30 bg-orange-500/10 text-orange-400 font-medium'>
            ⚠ {driftCount} {t('orchestrator.driftCount', { count: driftCount })}
          </span>
        )}
        <StatusBadge
          status={(env.status && env.status !== 'pending' ? env.status : 'verifying') as EnvStatus}
        />
        <FlagBadge flag={env.flag} migration={env.flag_migration} />
      </div>

      {
        /* Topologie — kdo s kým mluví.
          Filip 2026-06-02: \"šel by nějak graficky postihnout jek je proxy
          propojený do FE a na který BE je aktuálne FE propojen?\"

          Postavíme řetěz internet → proxy → app → backend z dvou zdrojů:
          1) npm.spec.target_service = \"$<svcName>\" → proxy ukazuje na svcName
          2) apphost.meta.lovable_supabase_url ↔ external.meta.url → FE→BE

          Když některý link chybí, řetěz se prostě zkrátí. */
      }
      {(() => {
        const npmEntry = svcEntries.find(([, s]) => s.module === 'npm');
        const apphostEntry = svcEntries.find(([, s]) => s.module === 'apphost');
        const dbhostEntry = svcEntries.find(([, s]) =>
          s.module === 'dbhost' || s.module === 'supabase'
        );
        const externalEntry = svcEntries.find(([, s]) => s.module === 'external');

        // FE → BE detekce s priority order:
        //   1) spec.active_backend (explicit) — pokud match konkrétního service key
        //   2) lovable_supabase_url match s external (= heuristika)
        //   3) první dbhost/supabase ve stacku
        //   4) první external ve stacku
        let feBeTarget: typeof externalEntry | typeof dbhostEntry | undefined;
        if (activeBackend) {
          feBeTarget = svcEntries.find(([k]) => k === activeBackend);
        }
        if (!feBeTarget && apphostEntry) {
          const supaUrl = apphostEntry[1].meta?.lovable_supabase_url as string | undefined;
          if (supaUrl && externalEntry) {
            const extUrl = externalEntry[1].meta?.url as string | undefined;
            if (extUrl && supaUrl.startsWith(extUrl.replace(/\/+$/, ''))) {
              feBeTarget = externalEntry;
            }
          }
          if (!feBeTarget && dbhostEntry) {
            feBeTarget = dbhostEntry; // lokální dbhost/supabase je default backend
          }
          if (!feBeTarget && externalEntry) {
            feBeTarget = externalEntry; // best-effort: jen 1 external → backend
          }
        }
        // Všechny ostatní BE služby = záložní (vidět nahoře v chainu vedle)

        // Chain: vždy začíná internet pokud existuje proxy s doménou;
        // jinak začínáme přímo apphost (= jen interní).
        const chain: Array<
          | { kind: 'internet' }
          | { kind: 'svc'; name: string; svc: typeof svcEntries[number][1]; label: string }
        > = [];
        const npmDomain = (npmEntry?.[1].meta?.domain as string | undefined) ??
          (npmEntry?.[1] as unknown as { meta?: { domain?: string } } | undefined)?.meta?.domain;
        if (npmEntry && npmDomain) {
          chain.push({ kind: 'internet' });
          chain.push({ kind: 'svc', name: npmEntry[0], svc: npmEntry[1], label: 'proxy' });
        }
        if (apphostEntry) {
          chain.push({
            kind: 'svc',
            name: apphostEntry[0],
            svc: apphostEntry[1],
            label: 'frontend',
          });
        }
        if (feBeTarget) {
          chain.push({ kind: 'svc', name: feBeTarget[0], svc: feBeTarget[1], label: 'backend' });
        }

        if (chain.length < 2) return null;

        return (
          <div className='bg-gray-900/40 rounded-xl border border-gray-800/40 p-4'>
            <div className='text-[9px] text-gray-600 uppercase tracking-wider mb-3 font-medium'>
              {t('orchestrator.dataFlow')}
            </div>
            <div className='flex items-center gap-2 flex-wrap'>
              {chain.map((step, i) => {
                const isLast = i === chain.length - 1;
                if (step.kind === 'internet') {
                  return (
                    <div key={`internet-${i}`} className='flex items-center gap-2'>
                      <div className='flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-700 bg-gray-900/60'>
                        <Globe size={14} className='text-blue-400' />
                        <span className='text-xs text-gray-300 font-medium'>
                          {t('orchestrator.internet')}
                        </span>
                      </div>
                      {!isLast && <span className='text-gray-600 text-lg'>→</span>}
                    </div>
                  );
                }
                const svcTech = TECH_CFG[step.svc.module] ?? TECH_CFG['apphost']!;
                const SvcIcon = SVC_ICONS[step.svc.module] ?? Server;
                const svcOk = step.svc.status === 'running';
                const stepMeta = step.svc.meta ?? {};
                // Per-step subtitle (= co nejvíc relevantní detail)
                let subtitle = '';
                if (step.svc.module === 'npm') {
                  subtitle = String(stepMeta.domain ?? '');
                } else if (step.svc.module === 'apphost') {
                  subtitle = `:${stepMeta.app_port ?? '?'} · ${stepMeta.app_type ?? 'app'}`;
                } else if (step.svc.module === 'external') {
                  const prov = stepMeta.provider as string | undefined;
                  subtitle = prov ? (EXTERNAL_PROVIDER_LABEL[prov] ?? prov) : 'cloud';
                } else if (step.svc.module === 'supabase' || step.svc.module === 'dbhost') {
                  subtitle = String(stepMeta.port ?? stepMeta.kong_http ?? '');
                }
                return (
                  <div key={`step-${i}`} className='flex items-center gap-2'>
                    <div
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 ${
                        svcOk ? `${svcTech.border} ${svcTech.bg}` : 'border-gray-700 bg-gray-900/40'
                      }`}
                    >
                      <SvcIcon size={14} className={svcOk ? svcTech.color : 'text-gray-500'} />
                      <div className='flex flex-col leading-tight'>
                        <span className='text-xs text-white font-bold'>{step.name}</span>
                        {subtitle && (
                          <span className='text-[9px] text-gray-500 font-mono'>{subtitle}</span>
                        )}
                      </div>
                      <span className='text-[9px] text-gray-500 uppercase ml-1'>{step.label}</span>
                    </div>
                    {!isLast && <span className='text-gray-600 text-lg'>→</span>}
                  </div>
                );
              })}
            </div>

            {
              /* "Záložní backendy" sub-sekce odstraněna — duplikovala info
                co je už v service kartě (Aktivní/Záložní badge v hlavičce). */
            }
          </div>
        );
      })()}

      {
        /* Node rectangles — fyzický server / cloud provider, kde běží.
          Cloud služby (module=external) jsou samostatná skupina s odlišnou
          stylizací (sky border + Cloud ikona), ne v "lokálním serveru". */
      }
      {sortedNodeEntries.map(([node, svcs]) => {
        const isCloud = node.startsWith('cloud:');
        const isRemoteNode = !isCloud && node !== 'local';
        const cloudProvider = isCloud ? node.slice(6) : '';
        const cloudProviderLabel = isCloud
          ? (EXTERNAL_PROVIDER_LABEL[cloudProvider] ?? cloudProvider)
          : '';
        const headerLabel = isCloud ? cloudProviderLabel : nodeLabel(node);
        return (
          <div
            key={node}
            className={`rounded-2xl border-2 ${
              isCloud
                ? 'border-sky-700/40 bg-sky-950/10'
                : isRemoteNode
                ? 'border-violet-700/40 bg-violet-950/10'
                : 'border-gray-700/50 bg-gradient-to-b from-gray-800/20 to-transparent'
            } p-3 sm:p-4`}
          >
            {/* Node header */}
            <div className='flex items-center gap-2 mb-3 pb-2 border-b border-gray-800/40 flex-wrap'>
              <div
                className={`w-2.5 h-2.5 rounded-full ${
                  isCloud ? 'bg-sky-500' : isRemoteNode ? 'bg-violet-500' : 'bg-emerald-500'
                }`}
              />
              {isCloud ? <Cloud size={14} className='text-sky-400' /> : (
                <Server
                  size={14}
                  className={isRemoteNode ? 'text-violet-400' : 'text-gray-500'}
                />
              )}
              <span className='text-xs font-bold text-white'>
                {headerLabel}
              </span>
              {isCloud && (
                <span className='text-[9px] uppercase font-bold text-sky-300 bg-sky-500/15 border border-sky-500/30 px-1.5 py-0.5 rounded'>
                  {t('orchestrator.cloud')}
                </span>
              )}
              {isRemoteNode && (
                <span className='text-[9px] uppercase font-bold text-violet-300 bg-violet-500/15 border border-violet-500/30 px-1.5 py-0.5 rounded'>
                  {t('orchestrator.remoteNode', 'remote')}
                </span>
              )}
              <span className='text-[10px] text-gray-600'>
                {svcs.length} {t('orchestrator.services')}
              </span>
              <span className='text-[10px] text-gray-600 ml-auto font-mono'>
                {svcs.filter(([, , drift]) => drift === 'running').length}/{svcs.length} running
              </span>
            </div>

            {/* Services inside node */}
            <div className='grid gap-2'>
              {svcs.map(([svcName, svc, driftState]) => {
                const tech = TECH_CFG[svc.module] ?? TECH_CFG['apphost']!;
                const Icon = SVC_ICONS[svc.module] ?? Server;
                const ok = driftState === 'running';
                const syncing = svc.status === 'syncing';
                const isMissing = driftState === 'missing';
                const isSpecOnly = driftState === 'spec_only';
                const isOrphan = driftState === 'orphan';
                // Ghost styling pro spec_only / missing — slot existuje (z definice),
                // ale runtime chybí. Šedý / oranžový rámec, 60% opacity ikona.
                const ghosted = isMissing || isSpecOnly;
                const meta = svc.meta ?? {};

                // Build detail info
                const details: Array<{ label: string; value: string; color?: string }> = [];
                if (meta.app_port) details.push({ label: 'port', value: String(meta.app_port) });
                if (meta.private_api) {
                  details.push({
                    label: 'API',
                    value: String(meta.private_api),
                    color: 'text-cyan-400',
                  });
                }
                if (meta.private_studio) {
                  details.push({
                    label: 'Studio',
                    value: String(meta.private_studio),
                    color: 'text-purple-400',
                  });
                }
                if (meta.kong_http) details.push({ label: 'Kong', value: `:${meta.kong_http}` });
                if (meta.running !== undefined) {
                  details.push({ label: 'containers', value: `${meta.running}/${meta.total}` });
                }

                // Proxy info.
                // U npm služby je doména/host_id v meta.domain / meta.npm_host_id.
                // U cílových služeb (apphost, supabase, ...) orch_status cross-linkuje
                // proxy_domain / proxy_npm_host_id / proxy_provider — tj. FE má napsanou
                // veřejnou URL přes NPM. Fallback chain pokrývá obě varianty.
                const proxyDomain = (meta.domain as string | undefined) ??
                  (meta.proxy_domain as string | undefined);
                const proxyId = meta.npm_host_id ?? meta.proxy_npm_host_id ??
                  meta.proxy_id ?? meta.external_host_id;
                const proxyProvider = (meta.provider as string | undefined) ??
                  (meta.proxy_provider as string | undefined);
                const externalUrl = svc.module === 'external'
                  ? (meta.url as string | undefined)
                  : undefined;

                const isExpanded = expanded === svcName;

                // Role label — odvozeno z modulu + kontextu env. Spec klíč
                // ("app", "db", "frontend", ...) je interní identifikátor, pro
                // uživatele má smysl ukázat *roli* (Frontend / Backend / Proxy /
                // Záloha). Apphost má dvě varianty:
                //   - SPA + samostatný BE (external/dbhost/supabase ve stacku) → Frontend
                //   - monolit bez BE (Laravel typicky) → App
                // Bez tohoto refactoru uživatel vidí jen "app" a netuší kterou
                // vrstvu mu to zobrazuje (legacy lovable wizard naming).
                const hasBackendSibling = svcEntries.some(([, s]) =>
                  s.module === 'external' || s.module === 'dbhost' || s.module === 'supabase'
                );
                const roleLabel: string = svc.module === 'apphost'
                  ? (hasBackendSibling ? t('orchestrator.roleFrontend') : t('orchestrator.roleApp'))
                  : svc.module === 'external'
                  ? t('orchestrator.roleBackendCloud')
                  : svc.module === 'dbhost'
                  ? t('orchestrator.roleDatabase')
                  : svc.module === 'supabase'
                  ? t('orchestrator.roleBackendSupabase')
                  : svc.module === 'npm'
                  ? t('orchestrator.roleProxy')
                  : svc.module === 'backup'
                  ? t('orchestrator.roleBackup')
                  : svc.module === 'dns'
                  ? 'DNS'
                  : svc.module;
                // Multi-backend visual emphasis — aktivní backend dostane
                // emerald 2px outer ring (vizuálně dominantní), záložní
                // backendy mají opacity-60 dimming. Bez tohoto byl badge
                // v hlavičce karty jediný cue, který Filip přehlédl ve
                // velkém node gridu.
                const isBackend = isBackendModule(svc.module);
                const isActiveBackend = isBackend && activeBackend === svcName;
                const isStandbyBackend = isBackend && activeBackend !== svcName &&
                  activeBackend !== undefined;
                return (
                  <div
                    key={svcName}
                    className={`rounded-xl border-2 transition-all overflow-hidden cursor-pointer ${
                      ok
                        ? `${tech.border} ${tech.bg}`
                        : syncing
                        ? 'border-yellow-500/20 bg-yellow-500/5'
                        : svc.status === 'error'
                        ? 'border-red-500/20 bg-red-500/5'
                        : isMissing
                        ? 'border-orange-500/40 bg-orange-500/5 border-dashed'
                        : isSpecOnly
                        ? 'border-gray-700 bg-gray-900/20 border-dashed'
                        : isOrphan
                        ? 'border-amber-500/40 bg-amber-500/5'
                        : 'border-gray-800 bg-gray-900/30'
                    } ${isExpanded ? 'ring-1 ring-blue-500/30' : ''} ${
                      isActiveBackend
                        ? 'ring-2 ring-emerald-500/60 ring-offset-2 ring-offset-gray-950'
                        : ''
                    } ${isStandbyBackend ? 'opacity-60' : ''} ${ghosted ? 'opacity-70' : ''}`}
                    onClick={() => setExpanded(isExpanded ? null : svcName)}
                  >
                    {/* Header — vždy viditelný */}
                    <div className='flex items-center gap-3 p-3'>
                      <div
                        className={`w-1.5 h-12 rounded-full flex-shrink-0 ${
                          ok
                            ? tech.color.replace('text-', 'bg-')
                            : syncing
                            ? 'bg-yellow-500'
                            : svc.status === 'error'
                            ? 'bg-red-500'
                            : 'bg-gray-700'
                        }`}
                      />
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          ok ? tech.bg : 'bg-gray-800/50'
                        }`}
                      >
                        {syncing
                          ? <Loader2 size={20} className='text-yellow-400 animate-spin' />
                          : (
                            <Icon
                              size={20}
                              className={ok
                                ? tech.color
                                : svc.status === 'error'
                                ? 'text-red-400'
                                : 'text-gray-600'}
                            />
                          )}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2'>
                          {
                            /* Primární: role (Frontend/Backend/Proxy/...).
                            Sekundární: spec key ("app"/"db"/...) jako mono code —
                            interní identifikátor pro reference v specu, ne hlavní
                            kontext pro uživatele. */
                          }
                          <span className='text-sm font-bold text-white'>{roleLabel}</span>
                          <code
                            title={t('orchestrator.specKeyTitle', { service: svcName })}
                            className='text-[10px] text-gray-500 font-mono bg-gray-800/50 px-1.5 py-0.5 rounded'
                          >
                            {svcName}
                          </code>
                          <span
                            className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${tech.color} ${tech.bg}`}
                          >
                            {tech.label}
                          </span>
                          {
                            /* App-type badge — apphost má v meta.app_type technologii
                            (react, laravel, ...). Bez ní by uživatel viděl jen
                            "app" a nepoznal o jakou aplikaci jde. */
                          }
                          {svc.module === 'apphost' && meta.app_type && (
                            <span
                              title={t('orchestrator.apphostRuntimeTitle', {
                                type: String(meta.app_type),
                              })}
                              className='text-[9px] px-1.5 py-0.5 rounded font-bold uppercase text-emerald-300 bg-emerald-500/10 border border-emerald-500/30'
                            >
                              {String(meta.app_type)}
                            </span>
                          )}
                          {
                            /* Active/standby badge pro backend služby (external,
                            supabase, dbhost). active_backend v env.spec určuje
                            kdo je teď wired do FE; ostatní BE jsou záložní. */
                          }
                          {isBackendModule(svc.module) && (
                            <span
                              title={activeBackend === svcName
                                ? t('orchestrator.activeBackendTitle')
                                : t('orchestrator.standbyBackendTitle')}
                              className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase border ${
                                activeBackend === svcName
                                  ? 'text-emerald-300 bg-emerald-500/15 border-emerald-500/40'
                                  : 'text-gray-400 bg-gray-700/40 border-gray-600/40'
                              }`}
                            >
                              {activeBackend === svcName
                                ? t('orchestrator.active')
                                : t('orchestrator.standby')}
                            </span>
                          )}
                          {
                            /* Propojit s FE network — pro local supabase bez
                            internal_url. Volá orch_connect_internal_backend
                            (docker network connect). Bez tohoto FE nedosáhne
                            k supabase z svého containeru (= separate network).
                            Tlačítko zmizí po úspěšném connect (= spec dostane
                            internal_url field). */
                          }
                          {svc.module === 'supabase' &&
                            !(env.spec.services[svcName] as { internal_url?: string } | undefined)
                              ?.internal_url &&
                            ok &&
                            (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (
                                    await themedConfirm(
                                      t('orchestrator.connectSupabaseConfirm', {
                                        service: svcName,
                                        kong: `http://${env.name}-${svcName}-supabase-kong:8000`,
                                      }),
                                    )
                                  ) {
                                    connectInternalBackend(svcName);
                                  }
                                }}
                                disabled={connecting !== null}
                                title={t('orchestrator.connectSupabaseTitle')}
                                className='text-[9px] px-2 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/20 font-bold uppercase disabled:opacity-40'
                              >
                                {connecting === svcName ? '…' : t('orchestrator.connectToFe')}
                              </button>
                            )}
                          {
                            /* Aktivovat tlačítko pro záložní backend.
                            Volá orch_swap_backend tool — rewrite FE .env
                            (VITE_SUPABASE_URL + _PUBLISHABLE_KEY), npm build,
                            restart apphost container. ~30-60s downtime.
                            Confirm pre-call kvůli destruktivnosti. */
                          }
                          {isBackendModule(svc.module) && activeBackend !== svcName && ok && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const targetSpec = env.spec.services[svcName] as
                                  | { internal_url?: string; url?: string; anon_key?: string }
                                  | undefined;
                                const targetUrl = svc.module === 'external'
                                  ? (meta.url as string | undefined)
                                  : (targetSpec?.internal_url ??
                                    `127.0.0.1:${meta.kong_http ?? meta.auth_api_port ?? '?'}`);
                                const msg = t('orchestrator.swapConfirm', {
                                  from: activeBackend ?? '?',
                                  to: svcName,
                                  url: targetUrl,
                                });
                                if (await themedConfirm(msg)) {
                                  let anonOverride: string | undefined;
                                  if (svc.module === 'external' && !targetSpec?.anon_key) {
                                    const k = await themedPrompt(
                                      t('orchestrator.anonKeyPrompt', { service: svcName }),
                                      '',
                                    );
                                    if (!k) return;
                                    anonOverride = k;
                                  }
                                  swapBackend(svcName, anonOverride);
                                }
                              }}
                              disabled={swapping !== null}
                              title={t('orchestrator.activateBackendTitle')}
                              className='text-[9px] px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/40 text-amber-300 hover:bg-amber-500/20 font-bold uppercase disabled:opacity-40'
                            >
                              {swapping === svcName ? '…' : t('orchestrator.activate')}
                            </button>
                          )}
                          {/* External service: provider badge + cloud icon */}
                          {svc.module === 'external' && (() => {
                            const provider = meta.provider as string | undefined;
                            const extType = meta.type as string | undefined;
                            const url = meta.url as string | undefined;
                            const providerLabel = provider
                              ? EXTERNAL_PROVIDER_LABEL[provider] ?? provider
                              : t('orchestrator.cloud');
                            return (
                              <span
                                title={url
                                  ? `${providerLabel}${extType ? ` (${extType})` : ''} — ${url}`
                                  : providerLabel}
                                className='flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase text-sky-300 bg-sky-500/15 border border-sky-500/40'
                              >
                                <Cloud size={9} />
                                {providerLabel}
                                {extType && (
                                  <span className='text-sky-400/70 normal-case'>· {extType}</span>
                                )}
                              </span>
                            );
                          })()}
                          <span
                            className={`text-[10px] font-bold uppercase ${
                              ok
                                ? 'text-green-400'
                                : syncing
                                ? 'text-yellow-400'
                                : svc.status === 'error'
                                ? 'text-red-400'
                                : isMissing
                                ? 'text-orange-400'
                                : isSpecOnly
                                ? 'text-gray-500'
                                : isOrphan
                                ? 'text-amber-400'
                                : 'text-gray-600'
                            }`}
                          >
                            {isMissing
                              ? t('orchestrator.driftMissing')
                              : isSpecOnly
                              ? t('orchestrator.driftNotApplied')
                              : isOrphan
                              ? 'ORPHAN'
                              : STATUS_LABEL[svc.status]
                              ? t(`orchestrator.${STATUS_LABEL[svc.status]}`)
                              : svc.status}
                          </span>
                        </div>
                        <div className='text-[10px] text-gray-500 font-mono'>{svc.instance}</div>
                      </div>
                      {
                        /* Per-service akce — nesahá na expand toggle.
                        External: pro odregistrace stejné tlačítko, jiný title.
                        Žádný lifecycle (start/stop) — cloud služba je out-of-scope. */
                      }
                      {(isMissing || isOrphan || ok) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            destroyService(svcName);
                          }}
                          disabled={destroying === svcName}
                          title={svc.module === 'external'
                            ? t('orchestrator.unregisterCloudTitle')
                            : t('orchestrator.removeServiceTitle')}
                          className='p-1.5 text-gray-500 hover:text-red-400 disabled:opacity-30 transition-colors'
                        >
                          {destroying === svcName
                            ? <Loader2 size={13} className='animate-spin' />
                            : <Trash2 size={13} />}
                        </button>
                      )}
                      <span className='text-gray-600 text-xs'>{isExpanded ? '▼' : '▶'}</span>
                    </div>

                    {/* Expanded detail — proxy, links, akce */}
                    {isExpanded && (
                      <div
                        className='border-t border-gray-800/30 px-4 py-3 space-y-3 bg-gray-900/30'
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Detaily */}
                        <div className='flex items-center gap-2 flex-wrap'>
                          {details.map((d, i) => (
                            <span
                              key={i}
                              className={`text-[10px] ${
                                d.color ?? 'text-gray-500'
                              } bg-gray-800/50 px-2 py-0.5 rounded`}
                            >
                              {d.label}: <span className='text-white'>{d.value}</span>
                            </span>
                          ))}
                          {!!svc.target_node && (
                            <span className='text-[10px] text-gray-500 bg-gray-800/50 px-2 py-0.5 rounded'>
                              {t('orchestrator.node')}:{' '}
                              <span className='text-white'>{svc.target_node}</span>
                            </span>
                          )}
                          {
                            /* Plugin link — otevři službu přímo v jejím modulu.
                            Místo duplikovaného inline editoru: přebíráme UI
                            z modulu (supabase, apphost, ...) kde editor existuje.

                            Pozor: param `instance` musí být skutečný instance
                            name (= envName-svcName, ne klíč z env.spec.services),
                            jinak modulová stránka instanci nenajde a hodí 404.

                            External nemá vlastní modulovou stránku (= cloud
                            registrace bez lokální správy) → link skryjeme. */
                          }
                          {svc.module !== 'external' && (
                            <a
                              href={moduleHref(svc.module, svc.instance)}
                              onClick={(e) => e.stopPropagation()}
                              className='text-[10px] px-2 py-0.5 rounded bg-blue-500/10 border border-blue-500/30 text-blue-300 hover:bg-blue-500/20'
                              title={t('orchestrator.openInModuleTitle', {
                                instance: svc.instance,
                                module: svc.module,
                              })}
                            >
                              ↗ {t('orchestrator.openIn', { module: svc.module })}
                            </a>
                          )}
                        </div>

                        {
                          /* Git & Build sekce — viditelná jen pro apphost s git_repo.
                          Ukazuje: odkud se buildí (gitlab repo + branch), v jakém
                          stavu je sync (lovable_hash vs local_hash) a kdy byl
                          poslední úspěšný build. Tlačítko Aktualizovat z gitu:
                          pro lovable instance → apphost.lovable.refresh_build
                          (= preferuje cestu se zachováním client.ts),
                          pro non-lovable apphost → apphost.deploy (= klasický
                          git pull + npm/composer + rebuild). */
                        }
                        {svc.module === 'apphost' && meta.git_repo && (() => {
                          const repo = String(meta.git_repo);
                          const branch = (meta.git_branch as string) || 'main';
                          const isLovable = !!meta.lovable_hash;
                          const inSync = meta.lovable_in_sync !== false;
                          const lastBuild = meta.lovable_updated_at as string | undefined;
                          const lovHash = meta.lovable_hash as string | undefined;
                          const locHash = meta.local_hash as string | undefined;
                          const isDeploying = deploying === svcName;
                          // Z full URL gitlab odeber username:token (= readable label).
                          const repoDisplay = repo.replace(/https?:\/\/[^@]+@/, 'https://');
                          return (
                            <div className='bg-gray-950/50 rounded-lg p-3 border border-gray-800/30 space-y-2'>
                              <div className='flex items-center justify-between gap-2'>
                                <div className='text-[9px] text-gray-600 uppercase tracking-wider font-medium'>
                                  Git & Build
                                </div>
                                <button
                                  onClick={() => deployService(svcName, svc.instance, isLovable)}
                                  disabled={isDeploying}
                                  title={isLovable
                                    ? 'apphost.lovable.refresh_build — git pull + npm build + swap dist + restart'
                                    : 'apphost.deploy — git pull + build + restart'}
                                  className='flex items-center gap-1.5 text-[10px] px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40'
                                >
                                  {isDeploying
                                    ? <Loader2 size={11} className='animate-spin' />
                                    : <Download size={11} />}
                                  {isDeploying
                                    ? t('orchestrator.building')
                                    : t('orchestrator.updateFromGit')}
                                </button>
                              </div>
                              <div className='flex items-center gap-2 flex-wrap'>
                                <GitBranch size={11} className='text-blue-400' />
                                <a
                                  href={repoDisplay}
                                  target='_blank'
                                  rel='noopener'
                                  className='text-[11px] text-blue-300 hover:text-blue-200 font-mono break-all'
                                >
                                  {repoDisplay}
                                </a>
                                <span className='text-[9px] bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded font-mono'>
                                  {branch}
                                </span>
                                {isLovable && (
                                  <span className='text-[9px] bg-purple-500/10 text-purple-300 border border-purple-500/30 px-1.5 py-0.5 rounded uppercase font-bold'>
                                    Lovable
                                  </span>
                                )}
                              </div>
                              {(lovHash || locHash) && (
                                <div className='flex items-center gap-3 text-[10px] text-gray-500 flex-wrap'>
                                  {locHash && (
                                    <span title={t('orchestrator.localHashTitle')}>
                                      {t('orchestrator.deployed')}:{' '}
                                      <span className='font-mono text-white'>
                                        {locHash.slice(0, 12)}
                                      </span>
                                    </span>
                                  )}
                                  {lovHash && lovHash !== locHash && (
                                    <span title={t('orchestrator.cloudHashTitle')}>
                                      {t('orchestrator.cloud').toLowerCase()}:{' '}
                                      <span className='font-mono text-amber-300'>
                                        {lovHash.slice(0, 12)}
                                      </span>
                                    </span>
                                  )}
                                  <span
                                    className={inSync
                                      ? 'text-green-400'
                                      : 'text-amber-400 font-bold'}
                                  >
                                    {inSync ? t('orchestrator.inSync') : t('orchestrator.drift')}
                                  </span>
                                  {lastBuild && (
                                    <span title={lastBuild}>
                                      {t('orchestrator.lastBuild')}:{' '}
                                      {new Date(lastBuild).toLocaleString('cs-CZ')}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Proxy sekce */}
                        <div className='bg-gray-950/50 rounded-lg p-3 border border-gray-800/30'>
                          <div className='text-[9px] text-gray-600 uppercase tracking-wider mb-2 font-medium'>
                            {t('orchestrator.proxyAccess')}
                          </div>
                          {proxyDomain
                            ? (
                              <div className='space-y-1.5'>
                                <div className='flex items-center gap-2'>
                                  <Globe size={12} className='text-blue-400' />
                                  <a
                                    href={`https://${proxyDomain}`}
                                    target='_blank'
                                    rel='noopener'
                                    className='text-sm text-blue-400 hover:text-blue-300 font-mono'
                                  >
                                    {proxyDomain}
                                  </a>
                                  {proxyProvider && (
                                    <span className='text-[9px] bg-gray-800 text-gray-500 px-1.5 py-0.5 rounded'>
                                      {proxyProvider}
                                    </span>
                                  )}
                                  {!!proxyId && (
                                    <span className='text-[9px] text-gray-600 font-mono'>
                                      #{String(proxyId)}
                                    </span>
                                  )}
                                  {
                                    /* Spravovat / přidat doménu — link na NPM modul
                                    se správným tier prefixem. Předchozí: jen
                                    read-only zobrazení, žádná možnost úprav.
                                    Použije proxyId pokud existuje (= hint pro
                                    NPM stránku jaký host preselectnout). */
                                  }
                                  <a
                                    href={proxyId
                                      ? `${moduleHref('npm', 'upstream')}&host=${proxyId}`
                                      : moduleHref('npm', 'upstream')}
                                    onClick={(e) => e.stopPropagation()}
                                    title={t('orchestrator.manageNpmTitle')}
                                    className='text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 border border-blue-500/30 text-blue-300 hover:bg-blue-500/20'
                                  >
                                    ↗ {t('orchestrator.manage')}
                                  </a>
                                </div>
                                <div className='text-[10px] text-gray-500'>
                                  {t('orchestrator.internet')} →{' '}
                                  {proxyProvider === 'external-npm' ? 'upstream NPM → ' : ''}
                                  {t('orchestrator.localNginx')} → {svcName}
                                </div>
                              </div>
                            )
                            : svc.module === 'external'
                            ? (
                              // Cloud služba: meta.url = veřejná adresa kam se obrací FE.
                              // Žádné "publikovat" — orchestrátor cloud nespravuje.
                              externalUrl
                                ? (
                                  <div className='space-y-1.5'>
                                    <div className='flex items-center gap-2'>
                                      <Cloud size={12} className='text-sky-400' />
                                      <a
                                        href={externalUrl}
                                        target='_blank'
                                        rel='noopener'
                                        className='text-sm text-sky-400 hover:text-sky-300 font-mono break-all'
                                      >
                                        {externalUrl}
                                      </a>
                                    </div>
                                    <div className='text-[10px] text-gray-500'>
                                      {t('orchestrator.cloudEndpoint')} — {EXTERNAL_PROVIDER_LABEL[
                                        (meta.provider as string) ?? ''
                                      ] ?? t('orchestrator.externalBackend')}
                                      {!!meta.probe_at && (
                                        <span className='ml-2'>
                                          · check {meta.probe_alive
                                            ? <span className='text-green-400'>OK</span>
                                            : <span className='text-red-400'>FAIL</span>}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )
                                : (
                                  <div className='text-[10px] text-gray-500 italic'>
                                    {t('orchestrator.externalNoUrl')}
                                  </div>
                                )
                            )
                            : (
                              <PublishPanel
                                module={svc.module}
                                instance={svc.instance}
                                port={Number(meta.app_port) || Number(meta.kong_http) || 3000}
                                projectId={projectId}
                                serviceKey={svcName}
                              />
                            )}
                          {!!meta.private_api && (
                            <a
                              href={String(meta.private_api)}
                              target='_blank'
                              rel='noopener'
                              className='text-[10px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1 mt-1'
                            >
                              <Globe size={9} /> API: {String(meta.private_api)}
                            </a>
                          )}
                          {!!meta.private_studio && (
                            <a
                              href={String(meta.private_studio)}
                              target='_blank'
                              rel='noopener'
                              className='text-[10px] text-purple-400 hover:text-purple-300 flex items-center gap-1 mt-1'
                            >
                              <Globe size={9} /> Studio: {String(meta.private_studio)}
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {
        /* "Řetěz služeb" sekce odstraněna — překrývala se s "Datový tok"
          nahoře, který zobrazuje stejnou informaci ale směrovaně
          (internet → proxy → FE → BE) a s lepšími detaily. */
      }
    </div>
  );
}

// ── Provoz Panel — operace nad nasazením ─────────────────────────────────────
// Náhled změn (orch_release_preview), update z GitLabu (orch_update),
// historie deployů (orch_deploy_history), rollback na zálohu (orch_rollback +
// backup.list). Konfigurace (backend / lock / auto-deploy / secrets) je
// přesunuta do NastaveniPanel ve Správě. Read toolů (history/backups) přes
// useQuery, write toolů přes async handlery se spinner/confirm patternem.

interface DeployEntry {
  env: string;
  commit_sha?: string;
  fe_commit?: string;
  be_commit?: string;
  compatible?: boolean;
  deployed_at: string;
  by?: string;
  schema_applied?: string[];
  functions_count?: number;
  backup_ref?: string;
  active_backend?: string;
  status: string;
  steps?: string[];
  trigger?: 'manual' | 'webhook' | 'rollback';
}

interface BackupEntry {
  backup_ref: string;
  tag: string;
  commit_sha?: string;
  reason?: string;
  created_at: string;
  kinds: string[];
  total_bytes: number;
}

interface UpdateResult {
  ok: boolean;
  commit_sha?: string;
  deploy_key?: string;
  backup_ref?: string;
  schema_applied?: number;
  functions_count?: number;
  active_backend?: string;
  steps?: string[];
  error?: string;
  destructive?: unknown;
  hint?: string;
}

interface RollbackResult {
  ok: boolean;
  rolled_back_to?: string;
  commit_sha?: string;
  deploy_key?: string;
  steps?: string[];
  locked?: boolean;
  error?: string;
}

// orch_release_preview — náhled změn před nasazením (read-only).
// frontend/migrations/functions přítomné jen když up_to_date === false;
// drift přítomný jen když env má source_connection.
interface ReleasePreview {
  ok: boolean;
  env?: string;
  branch?: string;
  deployed_commit?: string;
  target_commit?: string;
  behind: number;
  up_to_date: boolean;
  frontend?: { count: number; files: Array<{ status: string; path: string }> };
  migrations?: {
    count: number;
    destructive_count: number;
    incoming: Array<{ file: string; version: string; destructive: string[] }>;
  };
  functions?: { count: number; changed: string[] };
  drift?: {
    ok?: boolean;
    drift_found?: boolean;
    schema_vs_cloud?: {
      tables_in_cloud_not_onprem?: string[];
      column_drift?: Array<{ table: string; in_cloud_not_onprem: string[] }>;
    };
    functions?: unknown;
    migrations?: unknown;
  };
  error?: string;
}

const RELEASE_TRIGGER_CLS: Record<string, string> = {
  manual: 'text-blue-400 bg-blue-500/10 border-blue-900',
  webhook: 'text-purple-400 bg-purple-500/10 border-purple-900',
  rollback: 'text-orange-400 bg-orange-500/10 border-orange-900',
};

function fmtTs(ts?: string, locale?: string): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString(locale || undefined, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ProvozPanel({ env, onChanged }: {
  env: OrchestratorEnvStatus;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const mcpCall = useOrchCall();
  // ── Update z GitLabu (orch_update) ───────────────────────────────────────────
  const [updating, setUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState<UpdateResult | null>(null);
  const [destructiveConfirm, setDestructiveConfirm] = useState(false);
  const [skipBackup, setSkipBackup] = useState(false);
  const runUpdate = async (confirmDestructive: boolean) => {
    setUpdating(true);
    try {
      const r = await mcpCall<UpdateResult>('orchestrator.orch_update', {
        env_name: env.name,
        by: 'dashboard',
        ...(confirmDestructive ? { confirm_destructive: true } : {}),
        ...(skipBackup ? { skip_backup: true } : {}),
      });
      setUpdateResult(r);
      if (r.ok) {
        toast.success(
          t('orchestrator.updateOk', {
            commit: r.commit_sha ? ` · ${r.commit_sha.slice(0, 8)}` : '',
          }),
        );
        setDestructiveConfirm(false);
        onChanged();
      } else if (r.destructive !== undefined) {
        toast.error(t('orchestrator.updateBlockedDestructive'));
      } else {
        toast.error(r.error ?? t('orchestrator.updateFailed'));
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setUpdating(false);
    }
  };

  // ── Náhled změn (orch_release_preview) ───────────────────────────────────────
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<ReleasePreview | null>(null);
  const loadPreview = async (silent = false) => {
    setPreviewLoading(true);
    try {
      const r = await mcpCall<ReleasePreview>('orchestrator.orch_release_preview', {
        env_name: env.name,
      }, { silent });
      setPreview(r);
      if (!r.ok && !silent) toast.error(r.error ?? t('orchestrator.previewFailed'));
    } catch (e) {
      if (!silent) toast.error((e as Error).message);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    void loadPreview(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env.name]);

  const { data: opsData } = useQuery({
    queryKey: ['orch-project-ops', env.name, env.project_id],
    queryFn: () =>
      mcpCall<{
        ok?: boolean;
        operations?: Array<{
          slug?: string;
          project_id?: string;
          operation_type?: string;
          status?: string;
          phase?: string;
          error?: string;
          initiated_by?: string;
          initiated_by_label?: string;
          updated_at?: string;
          finished_at?: string;
        }>;
      }>('orchestrator.project_operation_list', { limit: 40 }, { silent: true }),
    staleTime: 15_000,
    refetchInterval: 20_000,
  });
  const operations = (opsData?.operations ?? []).filter((o) =>
    o.slug === env.name || (!!env.project_id && o.project_id === env.project_id)
  );

  // ── Deploy history (read přes useQuery) ──────────────────────────────────────
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['orch-deploy-history', env.name],
    queryFn: () =>
      mcpCall<{ ok: boolean; count?: number; deploys?: DeployEntry[] }>(
        'orchestrator.orch_deploy_history',
        {
          env_name: env.name,
          limit: 30,
        },
      ),
    refetchInterval: 30_000,
  });
  const deploys = historyData?.deploys ?? [];

  // ── Backups (read přes useQuery) ─────────────────────────────────────────────
  const { data: backupData, isLoading: backupLoading } = useQuery({
    queryKey: ['orch-backups', env.name],
    queryFn: () =>
      mcpCall<{ ok: boolean; count?: number; backups?: BackupEntry[] }>('backup.list', {
        env: env.name,
      }),
    refetchInterval: 60_000,
  });
  const backups = backupData?.backups ?? [];

  // ── Rollback (orch_rollback) ─────────────────────────────────────────────────
  const [rollbackTarget, setRollbackTarget] = useState<string | null>(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [rollbackResult, setRollbackResult] = useState<RollbackResult | null>(null);
  const runRollback = async (backupRef: string) => {
    setRollingBack(true);
    try {
      const r = await mcpCall<RollbackResult>('orchestrator.orch_rollback', {
        env_name: env.name,
        backup_ref: backupRef,
        confirm: true,
        by: 'dashboard',
      });
      setRollbackResult(r);
      if (r.ok) {
        toast.success(
          t('orchestrator.rollbackOk', {
            target: r.rolled_back_to ? ` → ${r.rolled_back_to}` : '',
          }),
        );
        setRollbackTarget(null);
        onChanged();
      } else toast.error(r.error ?? t('orchestrator.rollbackFailed'));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setRollingBack(false);
    }
  };

  return (
    <div className='space-y-4'>
      {/* ── Náhled změn (Release diff) ───────────────────────────────────────── */}
      <Card>
        <CardContent className='p-4'>
          <div className='flex items-center gap-3 flex-wrap mb-3'>
            <Eye size={13} className='text-cyan-400' />
            <div className='text-xs font-semibold text-cyan-400 uppercase tracking-wide'>
              {t('orchestrator.releaseDiff')}
            </div>
            <p className='text-[10px] text-gray-500'>
              {t('orchestrator.releaseDiffDesc')}
            </p>
            <button
              onClick={() => void loadPreview()}
              disabled={previewLoading}
              className='ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cyan-600/20 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-600/30 disabled:opacity-40 text-xs font-medium'
            >
              {previewLoading ? <Loader2 size={12} className='animate-spin' /> : <Eye size={12} />}
              {previewLoading ? t('orchestrator.loading') : t('orchestrator.loadPreview')}
            </button>
          </div>

          {preview && !preview.ok && preview.error && (
            <div className='text-[11px] text-amber-300/90 mb-2'>{preview.error}</div>
          )}
          {preview && preview.ok && (
            <div className='space-y-3'>
              {/* Commit delta + badge */}
              <div className='flex items-center gap-2 flex-wrap text-[11px]'>
                <GitBranch size={12} className='text-gray-500' />
                {preview.branch && (
                  <span className='text-[9px] bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded font-mono'>
                    {preview.branch}
                  </span>
                )}
                <span className='font-mono text-gray-300'>
                  {(preview.deployed_commit ?? '????????').slice(0, 8)}
                </span>
                <span className='text-gray-600'>→</span>
                <span className='font-mono text-gray-300'>
                  {(preview.target_commit ?? '????????').slice(0, 8)}
                </span>
                {preview.up_to_date
                  ? (
                    <span className='inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase text-green-300 bg-green-500/10 border-green-500/40'>
                      <CheckCircle2 size={10} /> {t('orchestrator.upToDate')}
                    </span>
                  )
                  : (
                    <span className='inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase text-amber-300 bg-amber-500/10 border-amber-500/40'>
                      <AlertCircle size={10} />{' '}
                      {t('orchestrator.commitsToDeploy', { count: preview.behind })}
                    </span>
                  )}
              </div>

              {/* Diff bloky (jen když je co nasazovat) */}
              {!preview.up_to_date && (
                <div className='grid gap-3 md:grid-cols-3'>
                  {/* FE */}
                  {preview.frontend && (
                    <div className='bg-gray-950/50 rounded-lg p-3 border border-gray-800/30'>
                      <div className='text-[9px] text-gray-600 uppercase tracking-wider font-medium mb-2'>
                        {t('orchestrator.feFiles', { count: preview.frontend.count })}
                      </div>
                      <div className='space-y-0.5 max-h-40 overflow-y-auto'>
                        {preview.frontend.files.slice(0, 30).map((f, i) => (
                          <div key={i} className='text-[10px] font-mono text-gray-400 flex gap-2'>
                            <span
                              className={f.status === 'A'
                                ? 'text-green-400'
                                : f.status === 'D'
                                ? 'text-red-400'
                                : 'text-amber-400'}
                            >
                              {f.status}
                            </span>
                            <span className='break-all'>{f.path}</span>
                          </div>
                        ))}
                        {preview.frontend.files.length > 30 && (
                          <div className='text-[10px] text-gray-600'>
                            +{preview.frontend.files.length - 30} {t('orchestrator.more')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Schema */}
                  {preview.migrations && (
                    <div className='bg-gray-950/50 rounded-lg p-3 border border-gray-800/30'>
                      <div className='text-[9px] text-gray-600 uppercase tracking-wider font-medium mb-2'>
                        {t('orchestrator.schemaMigrations', { count: preview.migrations.count })}
                      </div>
                      {preview.migrations.destructive_count > 0 && (
                        <div className='flex items-start gap-1.5 text-[10px] text-red-300 bg-red-500/10 border border-red-500/40 rounded px-2 py-1 mb-2'>
                          <AlertCircle size={11} className='mt-0.5 flex-shrink-0' />
                          <span>
                            {t('orchestrator.destructiveMigrationsWarning')}
                          </span>
                        </div>
                      )}
                      <div className='space-y-1 max-h-40 overflow-y-auto'>
                        {preview.migrations.incoming.map((m, i) => {
                          const isDestructive = (m.destructive ?? []).length > 0;
                          return (
                            <div key={i} className='text-[10px]'>
                              <div className='flex items-center gap-2'>
                                <span className='font-mono text-gray-300'>{m.version}</span>
                                {isDestructive && (
                                  <span
                                    title={(m.destructive ?? []).join(', ')}
                                    className='text-[8px] px-1 py-0.5 rounded border font-bold uppercase text-red-300 bg-red-500/10 border-red-500/40'
                                  >
                                    {t('orchestrator.destructive')}
                                  </span>
                                )}
                              </div>
                              <div className='font-mono text-gray-500 break-all'>{m.file}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Edge funkce */}
                  {preview.functions && (
                    <div className='bg-gray-950/50 rounded-lg p-3 border border-gray-800/30'>
                      <div className='text-[9px] text-gray-600 uppercase tracking-wider font-medium mb-2'>
                        {t('orchestrator.edgeFunctions')} · {preview.functions.count}
                      </div>
                      <div className='flex flex-wrap gap-1'>
                        {preview.functions.changed.map((fn, i) => (
                          <span
                            key={i}
                            className='text-[9px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-300 font-mono'
                          >
                            {fn}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* BE drift (jen když env má source_connection) */}
              {preview.drift && (
                <div className='bg-gray-950/50 rounded-lg p-3 border border-gray-800/30 space-y-2'>
                  {preview.drift.drift_found
                    ? (
                      <>
                        <div className='inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded border font-bold uppercase text-amber-300 bg-amber-500/10 border-amber-500/40'>
                          <AlertCircle size={11} /> {t('orchestrator.beDriftUntracked')}
                        </div>
                        {(preview.drift.schema_vs_cloud?.tables_in_cloud_not_onprem ?? []).length >
                            0 && (
                          <div className='flex flex-wrap gap-1 items-center'>
                            <span className='text-[9px] text-gray-600'>
                              {t('orchestrator.tablesOnlyInCloud')}
                            </span>
                            {(preview.drift.schema_vs_cloud?.tables_in_cloud_not_onprem ?? []).map(
                              (tbl, i) => (
                                <span
                                  key={i}
                                  className='text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 font-mono'
                                >
                                  {tbl}
                                </span>
                              ),
                            )}
                          </div>
                        )}
                        {(preview.drift.schema_vs_cloud?.column_drift ?? []).length > 0 && (
                          <div className='space-y-0.5'>
                            {(preview.drift.schema_vs_cloud?.column_drift ?? []).map((c, i) => (
                              <div key={i} className='text-[10px] text-gray-400'>
                                <span className='font-mono text-amber-300'>{c.table}</span>:{' '}
                                <span className='font-mono text-gray-500'>
                                  {(c.in_cloud_not_onprem ?? []).join(', ')}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        <p className='text-[9px] text-gray-600'>
                          {t('orchestrator.driftInternalTablesNote')}
                        </p>
                      </>
                    )
                    : (
                      <div className='inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded border font-bold uppercase text-green-300 bg-green-500/10 border-green-500/40'>
                        <CheckCircle2 size={11} /> {t('orchestrator.beNoDrift')}
                      </div>
                    )}
                </div>
              )}

              {!preview.up_to_date && (
                <p className='text-[10px] text-gray-500'>
                  {t('orchestrator.deployAfterCheck')}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Update z GitLabu (orch_update) ───────────────────────────────────── */}
      <Card>
        <CardContent className='p-4'>
          <div className='flex items-center gap-3 flex-wrap mb-3'>
            <div className='text-xs font-semibold text-green-400 uppercase tracking-wide'>
              {t('orchestrator.deployCurrentCommit')}
            </div>
            <p className='text-[10px] text-gray-500'>
              {t('orchestrator.deployCurrentCommitDesc')}
            </p>
          </div>
          <div className='flex items-center gap-3 flex-wrap'>
            <button
              onClick={() => runUpdate(false)}
              disabled={updating}
              className='flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 disabled:opacity-40 text-sm font-semibold text-white transition-colors'
            >
              {updating ? <Loader2 size={13} className='animate-spin' /> : <Rocket size={13} />}
              {updating ? t('orchestrator.deploying') : t('orchestrator.updateFromGitlab')}
            </button>
            <label className='flex items-center gap-2 cursor-pointer text-[11px] text-gray-400'>
              <input
                type='checkbox'
                checked={skipBackup}
                onChange={(e) => setSkipBackup(e.target.checked)}
                disabled={updating}
              />{' '}
              {t('orchestrator.skipBackup')}
            </label>
          </div>

          {/* Výsledek update */}
          {updateResult && (
            <div className='mt-3 space-y-2'>
              {/* Destruktivní migrace blokace */}
              {!updateResult.ok && updateResult.destructive !== undefined && (
                <div className='rounded-lg border border-red-500/40 bg-red-500/10 p-3'>
                  <div className='flex items-center gap-2 text-xs font-semibold text-red-400 mb-1'>
                    <AlertCircle size={13} /> {t('orchestrator.destructiveMigrationsBlocked')}
                  </div>
                  {updateResult.error && (
                    <p className='text-[11px] text-red-300/90'>{updateResult.error}</p>
                  )}
                  {updateResult.hint && (
                    <p className='text-[11px] text-gray-400 mt-1'>{updateResult.hint}</p>
                  )}
                  <pre className='mt-2 bg-gray-950 border border-gray-800 rounded p-2 text-[10px] font-mono text-gray-400 overflow-x-auto max-h-40 overflow-y-auto whitespace-pre-wrap'>
                    {JSON.stringify(updateResult.destructive, null, 2)}
                  </pre>
                  <label className='flex items-center gap-2 cursor-pointer text-[11px] text-red-300 mt-2'>
                    <input
                      type='checkbox'
                      checked={destructiveConfirm}
                      onChange={(e) => setDestructiveConfirm(e.target.checked)}
                    />{' '}
                    {t('orchestrator.destructiveConfirmLabel')}
                  </label>
                  <button
                    onClick={() => runUpdate(true)}
                    disabled={updating || !destructiveConfirm}
                    className='mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600/20 border border-red-500/40 text-red-300 hover:bg-red-600/30 disabled:opacity-40 text-xs font-semibold'
                  >
                    {updating
                      ? <Loader2 size={12} className='animate-spin' />
                      : <AlertCircle size={12} />}
                    {t('orchestrator.confirmDestructiveContinue')}
                  </button>
                </div>
              )}

              {/* Chyba bez destructive */}
              {!updateResult.ok && updateResult.destructive === undefined && updateResult.error && (
                <div className='rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-[11px] text-red-300'>
                  <span className='inline-flex items-center gap-1 font-semibold'>
                    <XCircle size={12} /> {updateResult.error}
                  </span>
                </div>
              )}

              {/* OK summary */}
              {updateResult.ok && (
                <div className='text-[11px] text-gray-400 flex items-center gap-3 flex-wrap'>
                  {updateResult.commit_sha && (
                    <span className='font-mono text-gray-300'>
                      {updateResult.commit_sha.slice(0, 8)}
                    </span>
                  )}
                  {updateResult.backup_ref && (
                    <span>{t('orchestrator.backup')}: {updateResult.backup_ref}</span>
                  )}
                  {updateResult.schema_applied !== undefined && (
                    <span>schema: {updateResult.schema_applied}</span>
                  )}
                  {updateResult.functions_count !== undefined && (
                    <span>{t('orchestrator.functions')}: {updateResult.functions_count}</span>
                  )}
                </div>
              )}

              {/* Steps */}
              {updateResult.steps && updateResult.steps.length > 0 && (
                <div className='space-y-1'>
                  {updateResult.steps.map((s, i) => (
                    <div key={i} className='flex items-start gap-2 text-[11px] text-gray-400'>
                      {updateResult.ok
                        ? <CheckCircle2 size={12} className='text-green-400 mt-0.5 flex-shrink-0' />
                        : <AlertCircle size={12} className='text-amber-400 mt-0.5 flex-shrink-0' />}
                      <span>{s}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {operations.length > 0 && (
        <Card>
          <CardContent className='p-0'>
            <div className='border-b border-gray-800 px-4 py-2.5 flex items-center gap-3'>
              <Zap size={13} className='text-gray-500' />
              <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
                {t('orchestrator.lifecycleOps', 'Operace projektu')}
              </span>
              <span className='text-[10px] text-gray-600'>{operations.length}</span>
            </div>
            <div className='divide-y divide-gray-800/40'>
              {operations.slice(0, 12).map((op, i) => (
                <div
                  key={`${op.project_id}-${op.updated_at}-${i}`}
                  className='px-4 py-2.5 flex items-center gap-3 text-[11px]'
                >
                  <span className='font-mono text-gray-300 w-20 shrink-0'>
                    {op.operation_type ?? '—'}
                  </span>
                  <span
                    className={op.status === 'completed'
                      ? 'text-green-400'
                      : op.status === 'failed'
                      ? 'text-red-400'
                      : 'text-amber-300'}
                  >
                    {op.status ?? '—'}
                  </span>
                  <span
                    className='text-gray-300 truncate max-w-[9rem]'
                    title={op.initiated_by_label || op.initiated_by || ''}
                  >
                    {op.initiated_by_label || op.initiated_by || '—'}
                  </span>
                  {op.phase && <span className='text-gray-600 truncate'>{op.phase}</span>}
                  <span className='ml-auto text-gray-500 font-mono whitespace-nowrap'>
                    {fmtTs(op.finished_at || op.updated_at, i18n.language)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Historie deployů ─────────────────────────────────────────────────── */}
      <Card>
        <CardContent className='p-0'>
          <div className='border-b border-gray-800 px-4 py-2.5 flex items-center gap-3'>
            <History size={13} className='text-gray-500' />
            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
              {t('orchestrator.versionsHistory')}
            </span>
            <span className='text-[10px] text-gray-600'>
              {deploys.length} {t('orchestrator.records')}
            </span>
          </div>
          {historyLoading
            ? (
              <div className='py-8 text-center text-gray-600 text-xs'>
                <Loader2 size={14} className='animate-spin inline mr-2' />
                {t('orchestrator.loading')}
              </div>
            )
            : deploys.length === 0
            ? (
              <div className='py-8 text-center text-gray-600 text-xs'>
                {t('orchestrator.noDeployments')}
              </div>
            )
            : (
              <>
                {/* Mobile cards */}
                <div className='md:hidden divide-y divide-gray-800/40'>
                  {deploys.map((d, i) => {
                    const ref = d.backup_ref ?? d.commit_sha;
                    return (
                      <div key={i} className='px-4 py-3 space-y-2'>
                        <div className='flex items-center justify-between gap-2 flex-wrap'>
                          <span className='text-[11px] text-gray-400 font-mono'>
                            {fmtTs(d.deployed_at, i18n.language)}
                          </span>
                          <span
                            className={d.status === 'ok' || d.status === 'success'
                              ? 'text-green-400 text-[11px]'
                              : d.status === 'error'
                              ? 'text-red-400 text-[11px]'
                              : 'text-gray-400 text-[11px]'}
                          >
                            {d.status}
                          </span>
                        </div>
                        <div className='flex flex-wrap gap-1.5 items-center'>
                          {d.trigger && (
                            <span
                              className={`text-[9px] px-1.5 py-0.5 rounded border font-semibold uppercase ${
                                RELEASE_TRIGGER_CLS[d.trigger] ?? 'text-gray-500 border-gray-800'
                              }`}
                            >
                              {d.trigger}
                            </span>
                          )}
                          {d.commit_sha && (
                            <span className='font-mono text-[10px] text-gray-300'>
                              {d.commit_sha.slice(0, 8)}
                            </span>
                          )}
                          {d.compatible === true && (
                            <span className='inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase text-green-300 bg-green-500/10 border-green-500/40'>
                              <CheckCircle2 size={10} /> {t('orchestrator.ok')}
                            </span>
                          )}
                          {d.compatible === false && (
                            <span className='inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase text-amber-300 bg-amber-500/10 border-amber-500/40'>
                              <AlertCircle size={10} /> {t('orchestrator.mismatch')}
                            </span>
                          )}
                        </div>
                        <div className='text-[10px] text-gray-600 flex flex-wrap gap-x-3'>
                          <span>
                            {t('orchestrator.colSchema')}: {d.schema_applied?.length ?? 0}
                          </span>
                          <span>{t('orchestrator.colFunctions')}: {d.functions_count ?? '—'}</span>
                          {d.backup_ref && (
                            <span className='font-mono truncate'>{d.backup_ref}</span>
                          )}
                        </div>
                        {ref && d.backup_ref && (
                          <button
                            onClick={() => {
                              setRollbackResult(null);
                              setRollbackTarget(d.backup_ref!);
                            }}
                            className='text-[10px] px-2.5 py-1 rounded-lg bg-orange-500/10 border border-orange-500/40 text-orange-300 font-semibold uppercase'
                          >
                            {t('orchestrator.rollback')}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Desktop table */}
                <div className='hidden md:block overflow-x-auto'>
                  <table className='w-full text-[11px]'>
                    <thead>
                      <tr className='text-gray-600 border-b border-gray-800/60'>
                        <th className='text-left font-medium px-4 py-2'>
                          {t('orchestrator.colTime')}
                        </th>
                        <th className='text-left font-medium px-2 py-2'>
                          {t('orchestrator.colTrigger')}
                        </th>
                        <th className='text-left font-medium px-2 py-2'>
                          {t('orchestrator.colCommit')}
                        </th>
                        <th className='text-left font-medium px-2 py-2'>
                          {t('orchestrator.colFeBe')}
                        </th>
                        <th className='text-left font-medium px-2 py-2'>
                          {t('orchestrator.colSchema')}
                        </th>
                        <th className='text-left font-medium px-2 py-2'>
                          {t('orchestrator.colFunctions')}
                        </th>
                        <th className='text-left font-medium px-2 py-2'>
                          {t('orchestrator.colBackup')}
                        </th>
                        <th className='text-left font-medium px-2 py-2'>
                          {t('orchestrator.colStatus')}
                        </th>
                        <th className='px-2 py-2'></th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-800/40'>
                      {deploys.map((d, i) => {
                        const ref = d.backup_ref ?? d.commit_sha;
                        return (
                          <tr key={i} className='text-gray-400'>
                            <td className='px-4 py-2 whitespace-nowrap'>
                              {fmtTs(d.deployed_at, i18n.language)}
                            </td>
                            <td className='px-2 py-2'>
                              {d.trigger && (
                                <span
                                  className={`text-[9px] px-1.5 py-0.5 rounded border font-semibold uppercase ${
                                    RELEASE_TRIGGER_CLS[d.trigger] ??
                                      'text-gray-500 border-gray-800'
                                  }`}
                                >
                                  {d.trigger}
                                </span>
                              )}
                            </td>
                            <td className='px-2 py-2 font-mono text-gray-300'>
                              {d.commit_sha ? d.commit_sha.slice(0, 8) : '—'}
                            </td>
                            <td className='px-2 py-2'>
                              {d.compatible === true
                                ? (
                                  <span className='inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase text-green-300 bg-green-500/10 border-green-500/40'>
                                    <CheckCircle2 size={10} /> {t('orchestrator.ok')}
                                  </span>
                                )
                                : d.compatible === false
                                ? (
                                  <span
                                    title={`FE@${(d.fe_commit ?? '????????').slice(0, 8)} vs BE@${
                                      (d.be_commit ?? '????????').slice(0, 8)
                                    }`}
                                    className='inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase text-amber-300 bg-amber-500/10 border-amber-500/40'
                                  >
                                    <AlertCircle size={10} /> {t('orchestrator.mismatch')}
                                  </span>
                                )
                                : <span className='text-gray-600'>—</span>}
                            </td>
                            <td className='px-2 py-2'>{d.schema_applied?.length ?? 0}</td>
                            <td className='px-2 py-2'>{d.functions_count ?? '—'}</td>
                            <td className='px-2 py-2 font-mono text-[10px] text-gray-500'>
                              {d.backup_ref ?? '—'}
                            </td>
                            <td className='px-2 py-2'>
                              <span
                                className={d.status === 'ok' || d.status === 'success'
                                  ? 'text-green-400'
                                  : d.status === 'error'
                                  ? 'text-red-400'
                                  : 'text-gray-400'}
                              >
                                {d.status}
                              </span>
                            </td>
                            <td className='px-2 py-2 text-right'>
                              {ref && (
                                <button
                                  onClick={() => {
                                    setRollbackResult(null);
                                    setRollbackTarget(d.backup_ref ?? ref);
                                  }}
                                  disabled={!d.backup_ref}
                                  title={d.backup_ref
                                    ? t('orchestrator.rollbackToBackupTitle')
                                    : t('orchestrator.noBackupRollbackTitle')}
                                  className='text-[9px] px-2 py-0.5 rounded bg-orange-500/10 border border-orange-500/40 text-orange-300 hover:bg-orange-500/20 font-bold uppercase disabled:opacity-30'
                                >
                                  {t('orchestrator.rollback')}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
        </CardContent>
      </Card>

      {/* ── Zálohy + rollback ────────────────────────────────────────────────── */}
      <Card>
        <CardContent className='p-0'>
          <div className='border-b border-gray-800 px-4 py-2.5 flex items-center gap-3'>
            <Archive size={13} className='text-gray-500' />
            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
              {t('orchestrator.backups')}
            </span>
            <span className='text-[10px] text-gray-600'>
              {backups.length} {t('orchestrator.records')}
            </span>
          </div>

          {/* Rollback confirm UI */}
          {rollbackTarget && (
            <div className='border-b border-red-900/40 bg-red-500/5 px-4 py-3'>
              <div className='flex items-center gap-2 text-xs font-semibold text-red-400 mb-1'>
                <AlertCircle size={13} /> {t('orchestrator.rollbackWarning')}
              </div>
              <p className='text-[11px] text-gray-400 mb-2'>
                {t('orchestrator.target')}:{' '}
                <span className='font-mono text-gray-300'>{rollbackTarget}</span>
              </p>
              <div className='flex items-center gap-2'>
                <button
                  onClick={() => runRollback(rollbackTarget)}
                  disabled={rollingBack}
                  className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-600/20 border border-red-500/40 text-red-300 hover:bg-red-600/30 disabled:opacity-40 text-xs font-semibold'
                >
                  {rollingBack
                    ? <Loader2 size={12} className='animate-spin' />
                    : <RefreshCw size={12} />}
                  {t('orchestrator.confirmRollback')}
                </button>
                <button
                  onClick={() => setRollbackTarget(null)}
                  disabled={rollingBack}
                  className='px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white disabled:opacity-40 transition-colors'
                >
                  {t('orchestrator.cancel')}
                </button>
              </div>
              {rollbackResult && rollbackResult.steps && rollbackResult.steps.length > 0 && (
                <div className='mt-2 space-y-1'>
                  {rollbackResult.steps.map((s, i) => (
                    <div key={i} className='flex items-start gap-2 text-[11px] text-gray-400'>
                      <CheckCircle2 size={12} className='text-green-400 mt-0.5 flex-shrink-0' />
                      <span>{s}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {backupLoading
            ? (
              <div className='py-8 text-center text-gray-600 text-xs'>
                <Loader2 size={14} className='animate-spin inline mr-2' />
                {t('orchestrator.loading')}
              </div>
            )
            : backups.length === 0
            ? (
              <div className='py-8 text-center text-gray-600 text-xs'>
                {t('orchestrator.noBackups')}
              </div>
            )
            : (
              <div className='divide-y divide-gray-800/40'>
                {backups.map((b) => (
                  <div key={b.backup_ref} className='px-4 py-3 flex items-center gap-3'>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <span className='font-mono text-[11px] text-gray-300'>{b.backup_ref}</span>
                        {b.tag && (
                          <span className='text-[9px] px-1.5 py-0.5 rounded border border-gray-700 bg-gray-800 text-gray-400'>
                            {b.tag}
                          </span>
                        )}
                      </div>
                      <div className='text-[10px] text-gray-600 mt-0.5'>
                        {fmtTs(b.created_at)} · {b.kinds.join(', ')} ·{' '}
                        {(b.total_bytes / 1024 / 1024).toFixed(1)} MB
                        {b.reason ? ` · ${b.reason}` : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setRollbackResult(null);
                        setRollbackTarget(b.backup_ref);
                      }}
                      className='text-[9px] px-2 py-0.5 rounded bg-orange-500/10 border border-orange-500/40 text-orange-300 hover:bg-orange-500/20 font-bold uppercase'
                    >
                      Rollback
                    </button>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Nastavení Panel — konfigurace prostředí ──────────────────────────────────
// Backend switch (orch_swap_backend), produkční zámek (orch_lock), auto-deploy
// (orch_autodeploy), proklik na správu edge funkcí / secrets v Supabase modulu
// a pokročilý raw spec editor. Renderuje se ve
// Správě nad destroy/delete UI. Self-contained: vlastní state + handlery.

function NastaveniPanel({ env, onChanged }: {
  env: OrchestratorEnvStatus;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const mcpCall = useOrchCall();
  // ── Derived service keys ─────────────────────────────────────────────────────
  const specServices = ((env.spec as unknown as {
    services?: Record<string, { module?: string; [k: string]: unknown }>;
  }).services) ?? {};
  const backendServiceKey = Object.keys(specServices).find(
    (k) => specServices[k]?.module === 'supabase',
  );
  const backendInstance = backendServiceKey ? `${env.name}-${backendServiceKey}` : undefined;

  // ── Backend + produkční zámek (merged) — stav ze spec ───────────────────────
  // Jeden zdroj pravdy pro sloučenou kartu: aktivní backend (key), aktivní mód
  // (same-origin proxy / direct cloud / node-tls lokální) a produkční zámek.
  const sp = env.spec as unknown as {
    active_backend?: string;
    active_mode?: string;
    locked?: boolean;
    lock_reason?: string;
  };
  const locked = sp.locked === true;
  const lockReason = sp.lock_reason;

  // Service keys odvozené z modulu (ne z názvu klíče): cloud = external,
  // on-prem = supabase. Stejné backend služby, dva on-prem módy (proxy/lokální).
  const cloudKey = Object.keys(specServices).find(
    (k) => specServices[k]?.module === 'external',
  );
  const onpremKey = Object.keys(specServices).find(
    (k) => specServices[k]?.module === 'supabase',
  );

  // ── Lock toggle (orch_lock) ──────────────────────────────────────────────────
  // Zapnutí volitelně s důvodem (inline input); default "produkce".
  const [lockBusy, setLockBusy] = useState(false);
  const [lockInputOpen, setLockInputOpen] = useState(false);
  const [lockReasonInput, setLockReasonInput] = useState('');
  const setLock = async (nextLocked: boolean, reason?: string) => {
    setLockBusy(true);
    try {
      const r = await mcpCall<
        { ok: boolean; locked?: boolean; lock_reason?: string; error?: string }
      >(
        'orchestrator.orch_lock',
        { env_name: env.name, locked: nextLocked, ...(reason ? { reason } : {}) },
      );
      if (r.ok) {
        toast.success(
          nextLocked ? t('orchestrator.productionLocked') : t('orchestrator.productionUnlocked'),
        );
        setLockInputOpen(false);
        setLockReasonInput('');
        onChanged();
      } else toast.error(r.error ?? t('orchestrator.lockFailed'));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLockBusy(false);
    }
  };

  // ── Auto-deploy (orch_autodeploy) ────────────────────────────────────────────
  // Čteme konfigurovaný stav ze spec.auto_deploy (gitlab_project_id / branch /
  // connection_id / enabled). Token input ukazujeme JEN při zapínání bez
  // existující konfigurace — jinak by prázdné pole vypadalo jako "nenastaveno".
  const ad = (env.spec as unknown as {
    auto_deploy?: {
      enabled?: boolean;
      branch?: string;
      gitlab_project_id?: number;
      connection_id?: string;
    };
  }).auto_deploy;
  const autoDeployConfigured = ad !== undefined;
  const autoDeployEnabled = ad?.enabled === true;
  const [autoDeployBusy, setAutoDeployBusy] = useState(false);
  const [gitlabTokenInput, setGitlabTokenInput] = useState('');
  const setAutoDeploy = async (nextEnabled: boolean) => {
    setAutoDeployBusy(true);
    try {
      const needsToken = nextEnabled && !autoDeployConfigured;
      const r = await mcpCall<{
        ok: boolean;
        enabled?: boolean;
        branch?: string;
        hook_id?: number;
        error?: string;
      }>('orchestrator.orch_autodeploy', {
        env_name: env.name,
        enabled: nextEnabled,
        ...(needsToken ? { gitlab_token: gitlabTokenInput.trim() } : {}),
      });
      if (r.ok) {
        toast.success(
          nextEnabled ? t('orchestrator.autoDeployEnabled') : t('orchestrator.autoDeployDisabled'),
        );
        setGitlabTokenInput('');
        onChanged();
      } else toast.error(r.error ?? t('orchestrator.autoDeployChangeFailed'));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setAutoDeployBusy(false);
    }
  };

  // ── Backend switch (orch_swap_backend) — radio-style 3 tlačítka ──────────────
  // Klíč busy stavu = `${targetKey}:${mode}` (jedno tlačítko najednou).
  const [swapBusy, setSwapBusy] = useState<string | null>(null);
  const swap = async (targetKey: string, mode: string) => {
    if (sp.locked) return; // pojistka — při locku nejde (tlačítka jsou stejně disabled)
    setSwapBusy(`${targetKey}:${mode}`);
    try {
      const r = await mcpCall<{ ok: boolean; error?: string }>(
        'orchestrator.orch_swap_backend',
        {
          env_name: env.name,
          target_backend: targetKey,
          mode,
        },
      );
      if (r.ok) {
        toast.success(t('orchestrator.backendSwitched'));
        onChanged();
      } else toast.error(r.error ?? t('orchestrator.switchFailed'));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSwapBusy(null);
    }
  };

  // Node hostname pro sub-label lokálního módu (https://<node>:auth_api).
  const onpremNode = onpremKey
    ? ((specServices[onpremKey] as { target_node?: string } | undefined)?.target_node ?? 'node')
    : 'node';

  // Definice 3 tlačítek (radio) — vykreslí se v daném pořadí. Aktivní = přesně
  // jedno (detekce z active_backend + active_mode). Cloud jen pokud cloudKey
  // existuje; proxy/lokální jen pokud onpremKey existuje.
  type BackendBtn = {
    id: string;
    label: string;
    sub: string;
    targetKey: string;
    mode: string;
    active: boolean;
  };
  const backendBtns: BackendBtn[] = [];
  if (cloudKey) {
    backendBtns.push({
      id: 'cloud',
      label: t('orchestrator.cloud'),
      sub: 'Lovable Cloud',
      targetKey: cloudKey,
      mode: 'direct',
      active: sp.active_backend === cloudKey,
    });
  }
  if (onpremKey) {
    backendBtns.push({
      id: 'proxy',
      label: t('orchestrator.onpremProxy'),
      sub: t('orchestrator.onpremProxySub'),
      targetKey: onpremKey,
      mode: 'same-origin',
      active: sp.active_backend === onpremKey &&
        (sp.active_mode === 'same-origin' || sp.active_mode == null),
    });
    backendBtns.push({
      id: 'local',
      label: t('orchestrator.onpremLocal'),
      sub: t('orchestrator.onpremLocalSub', { node: onpremNode }),
      targetKey: onpremKey,
      mode: 'node-tls',
      active: sp.active_backend === onpremKey && sp.active_mode === 'node-tls',
    });
  }

  return (
    <div className='space-y-4'>
      {/* ── Backend (3-button selector + Produkce toggle) ───────────────────── */}
      <Card className={locked ? 'border-amber-900/40' : undefined}>
        <CardContent className='p-4'>
          {/* Hlavička: titulek + Produkce toggle vpravo */}
          <div className='flex items-center gap-3 flex-wrap mb-3'>
            <div className='text-xs font-semibold text-emerald-400 uppercase tracking-wide'>
              Backend
            </div>
            {/* Produkce toggle (orch_lock). ON = amber/red, zamkne přepínání. */}
            <button
              onClick={() => {
                if (locked) setLock(false);
                else setLockInputOpen(true);
              }}
              disabled={lockBusy}
              title={locked
                ? t('orchestrator.productionLockedTitle')
                : t('orchestrator.lockProductionTitle')}
              className={`ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 transition-colors ${
                locked
                  ? 'bg-amber-600/20 border-amber-500/40 text-amber-300 hover:bg-amber-600/30'
                  : 'border-gray-700 bg-gray-800 text-gray-300 hover:text-white'
              }`}
            >
              {lockBusy ? <Loader2 size={12} className='animate-spin' /> : <Shield size={12} />}
              <span>{t('orchestrator.production')}</span>
              {/* Vizuální switch-track */}
              <span
                className={`relative inline-block w-8 h-4 rounded-full transition-colors ${
                  locked ? 'bg-amber-500/70' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
                    locked ? 'left-4' : 'left-0.5'
                  }`}
                />
              </span>
            </button>
          </div>

          {/* Reason input — jen při zapínání zámku */}
          {!locked && lockInputOpen && (
            <div className='flex items-center gap-2 mb-3'>
              <input
                type='text'
                value={lockReasonInput}
                onChange={(e) => setLockReasonInput(e.target.value)}
                placeholder={t('orchestrator.lockReasonPlaceholder')}
                className='flex-1 bg-gray-950 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white placeholder-gray-600'
              />
              <button
                onClick={() =>
                  setLock(
                    true,
                    lockReasonInput.trim() || 'produkce',
                  )} /* DB hodnota — nepřekládat */
                disabled={lockBusy}
                className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-600/20 border border-amber-500/30 text-amber-400 hover:bg-amber-600/30 disabled:opacity-40 text-xs font-medium'
              >
                {lockBusy ? <Loader2 size={12} className='animate-spin' /> : <Shield size={12} />}
                {t('orchestrator.confirm')}
              </button>
              <button
                onClick={() => {
                  setLockInputOpen(false);
                  setLockReasonInput('');
                }}
                className='px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white transition-colors'
              >
                {t('orchestrator.cancel')}
              </button>
            </div>
          )}

          {/* Lock note + reason */}
          {locked && (
            <div className='mb-3 text-[11px] text-amber-400/90'>
              <span className='inline-flex items-center gap-1 font-medium'>
                <Shield size={11} /> {t('orchestrator.lockedBackendSwitch')}
              </span>
              {lockReason && <span className='ml-2 text-amber-400/70'>· {lockReason}</span>}
            </div>
          )}

          {/* 3 tlačítka (radio) — greyed když locked */}
          <div className='flex flex-wrap gap-2'>
            {backendBtns.map((b) => {
              const busyKey = `${b.targetKey}:${b.mode}`;
              const isBusy = swapBusy === busyKey;
              const disabled = b.active || locked || swapBusy !== null;
              return (
                <button
                  key={b.id}
                  disabled={disabled}
                  onClick={() => swap(b.targetKey, b.mode)}
                  title={locked
                    ? t('orchestrator.lockedByProduction')
                    : b.active
                    ? t('orchestrator.currentlyConnectedBackend')
                    : t('orchestrator.switchToBackend', { label: b.label })}
                  className={`flex flex-col items-start text-xs px-3 py-2 rounded border min-w-[140px] transition-colors ${
                    b.active
                      ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300 cursor-default'
                      : 'border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700'
                  } ${locked ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className='flex items-center gap-1 font-medium'>
                    {isBusy && <Loader2 size={12} className='animate-spin' />}
                    {b.active && !isBusy && <span>✓</span>}
                    {b.label}
                  </span>
                  <span className='text-[10px] text-gray-500 font-normal mt-0.5'>{b.sub}</span>
                </button>
              );
            })}
            {backendBtns.length === 0 && (
              <span className='text-[11px] text-gray-600'>{t('orchestrator.noBackendInSpec')}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Auto-deploy (orch_autodeploy) ────────────────────────────────────── */}
      <Card>
        <CardContent className='p-4'>
          <div className='flex items-center gap-3 flex-wrap'>
            <div className='text-xs font-semibold text-blue-400 uppercase tracking-wide'>
              Auto-deploy
            </div>
            {autoDeployConfigured
              ? (
                <span
                  className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-bold ${
                    autoDeployEnabled
                      ? 'text-green-300 bg-green-500/15 border-green-500/40'
                      : 'text-gray-400 bg-gray-800 border-gray-700'
                  }`}
                >
                  <GitBranch size={11} /> {t('orchestrator.gitlabProject')}{' '}
                  {ad?.gitlab_project_id ?? '—'} · branch {ad?.branch ?? 'main'} ·{' '}
                  {autoDeployEnabled ? t('orchestrator.on') : t('orchestrator.off')}
                </span>
              )
              : (
                <span className='inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-medium text-gray-400 bg-gray-800 border-gray-700'>
                  <GitBranch size={11} /> {t('orchestrator.notConfigured')}
                </span>
              )}
            <div className='ml-auto'>
              {autoDeployConfigured && (
                <button
                  onClick={() => setAutoDeploy(!autoDeployEnabled)}
                  disabled={autoDeployBusy}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs disabled:opacity-40 transition-colors ${
                    autoDeployEnabled
                      ? 'border-gray-700 text-gray-300 hover:text-white'
                      : 'bg-blue-600/20 border-blue-500/30 text-blue-400 hover:bg-blue-600/30 font-medium'
                  }`}
                >
                  {autoDeployBusy
                    ? <Loader2 size={12} className='animate-spin' />
                    : <GitBranch size={12} />}
                  {autoDeployEnabled ? t('orchestrator.disable') : t('orchestrator.enable')}
                </button>
              )}
            </div>
          </div>
          <p className='text-[11px] text-gray-500 mt-2'>
            {t('orchestrator.autoDeployLockedNote')}
          </p>
          {/* Token input jen při PRVNÍM zapnutí (žádná existující konfigurace). */}
          {!autoDeployConfigured && (
            <div className='flex items-center gap-2 mt-3'>
              <input
                type='password'
                value={gitlabTokenInput}
                onChange={(e) => setGitlabTokenInput(e.target.value)}
                placeholder={t('orchestrator.gitlabPatPlaceholder')}
                className='flex-1 bg-gray-950 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white placeholder-gray-600 font-mono'
              />
              <button
                onClick={() => setAutoDeploy(true)}
                disabled={autoDeployBusy || gitlabTokenInput.trim() === ''}
                className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 disabled:opacity-40 text-xs font-medium'
              >
                {autoDeployBusy
                  ? <Loader2 size={12} className='animate-spin' />
                  : <GitBranch size={12} />}
                {t('orchestrator.enableAutoDeploy')}
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {
        /* ── Zálohy projektu ───────────────────────────────────────────────────
          Dosud šlo v projektu zálohy jen VIDĚT (záložka Provoz) a udělat
          rollback — zapnout nebo vyladit plán nešlo nikde. */
      }
      {env.project_id && <ProjectBackupCard projectId={env.project_id} call={mcpCall} />}

      {/* ── Edge funkce / Secrets ────────────────────────────────────────────── */}
      <Card>
        <CardContent className='p-4'>
          <div className='flex items-center gap-2 mb-3'>
            <GitBranch size={13} className='text-gray-500' />
            <span className='text-xs font-semibold text-gray-400 uppercase tracking-wide'>
              {t('orchestrator.edgeFunctionsSecrets')}
            </span>
          </div>

          {backendInstance
            ? (
              <Link
                to={`/modules/supabase?instance=${backendInstance}`}
                className='inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 text-xs font-medium'
              >
                {t('orchestrator.openInstanceInSupabase')}
              </Link>
            )
            : (
              <p className='text-[11px] text-amber-400/80'>
                {t('orchestrator.noSupabaseBackend')}
              </p>
            )}

          <p className='text-[10px] text-gray-500 mt-3'>
            {t('orchestrator.edgeFunctionsSecretsNote')}
          </p>
        </CardContent>
      </Card>

      {/* ── Pokročilé: Spec (raw JSON editor) ────────────────────────────────── */}
      <Card>
        <CardContent className='p-4'>
          <details>
            <summary className='cursor-pointer text-xs font-semibold text-gray-400 uppercase tracking-wide select-none'>
              {t('orchestrator.advancedSpec')}
            </summary>
            <div className='mt-3'>
              <SpecPanel env={env} onUpdated={onChanged} />
            </div>
          </details>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Spec Panel ────────────────────────────────────────────────────────────────

function SpecPanel({ env, onUpdated }: { env: OrchestratorEnvStatus; onUpdated: () => void }) {
  const mcpCall = useOrchCall();
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);
  const [specText, setSpecText] = useState(JSON.stringify(env.spec, null, 2));
  const [parseError, setParseError] = useState('');

  const saveMut = useMutation({
    mutationFn: (spec: unknown) => mcpCall('orchestrator.orch_define', { name: env.name, spec }),
    onSuccess: () => {
      toast.success(t('orchestrator.specSaved'));
      setEditMode(false);
      onUpdated();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const handleSave = () => {
    try {
      const spec = JSON.parse(specText);
      setParseError('');
      saveMut.mutate(spec);
    } catch (e) {
      setParseError((e as Error).message);
    }
  };

  return (
    <div className='space-y-3'>
      <div className='flex items-center gap-2'>
        <span className='text-xs text-gray-500'>{t('orchestrator.declarativeSpec')}</span>
        <div className='ml-auto flex gap-2'>
          {!editMode && (
            <button
              onClick={() => {
                setSpecText(JSON.stringify(env.spec, null, 2));
                setEditMode(true);
              }}
              className='px-3 py-1.5 text-xs rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors'
            >
              {t('orchestrator.edit')}
            </button>
          )}
          <button
            onClick={() => {
              const blob = new Blob([JSON.stringify(env.spec, null, 2)], {
                type: 'application/json',
              });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `${env.name}-spec.json`;
              a.click();
            }}
            className='flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors'
          >
            <Download size={11} /> {t('orchestrator.export')}
          </button>
        </div>
      </div>

      {editMode
        ? (
          <>
            <textarea
              value={specText}
              onChange={(e) => {
                setSpecText(e.target.value);
                setParseError('');
              }}
              rows={20}
              spellCheck={false}
              className='w-full bg-gray-950 border border-gray-700 rounded-xl px-4 py-3 text-xs font-mono text-white focus:outline-none focus:border-blue-500 resize-none'
            />
            {parseError && (
              <p className='text-xs text-red-400 flex items-center gap-1'>
                <AlertCircle size={11} />
                {parseError}
              </p>
            )}
            <div className='flex gap-2 justify-end'>
              <button
                onClick={() => setEditMode(false)}
                className='px-3 py-1.5 text-xs text-gray-400 hover:text-white transition-colors'
              >
                {t('orchestrator.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saveMut.isPending}
                className='px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded-lg transition-colors'
              >
                {saveMut.isPending ? t('orchestrator.saving') : t('orchestrator.saveSpec')}
              </button>
            </div>
          </>
        )
        : (
          <pre className='bg-gray-950 border border-gray-800 rounded-xl p-4 text-xs font-mono text-gray-300 overflow-x-auto max-h-[500px] overflow-y-auto whitespace-pre-wrap'>
          {JSON.stringify(env.spec, null, 2) || '{}'}
          </pre>
        )}
    </div>
  );
}

// ── Event Log Panel ───────────────────────────────────────────────────────────

function EventLogPanel({ events }: { events: EnvEvent[] }) {
  const { t, i18n } = useTranslation();
  return (
    <div className='space-y-2'>
      {events.length === 0 && (
        <EmptyState
          className='py-8'
          title={t('orchestrator.noEvents')}
        />
      )}
      {events.length > 0 && (
        <>
          <div className='md:hidden space-y-2'>
            {events.map((evt) => (
              <div
                key={evt.id}
                className='rounded-xl border border-gray-800 bg-gray-950/40 px-3 py-2.5 space-y-1'
              >
                <div className='flex items-center justify-between gap-2'>
                  <span className='text-[10px] font-mono text-gray-600'>
                    {new Date(evt.created_at).toLocaleString(i18n.language)}
                  </span>
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      evt.status === 'ok'
                        ? 'bg-green-900/50 text-green-400'
                        : 'bg-red-900/50 text-red-400'
                    }`}
                  >
                    {evt.action}
                  </span>
                </div>
                <div className='text-xs font-mono text-gray-300'>{evt.service ?? '—'}</div>
                <div className='text-[11px] text-gray-400 break-words'>{evt.message}</div>
              </div>
            ))}
          </div>
          <div className='hidden md:block border border-gray-800 rounded-xl overflow-hidden'>
            <table className='w-full text-xs'>
              <thead>
                <tr className='border-b border-gray-800 bg-gray-900/30'>
                  <th className='text-left px-4 py-2 text-gray-600'>{t('orchestrator.colTime')}</th>
                  <th className='text-left px-4 py-2 text-gray-600'>
                    {t('orchestrator.colService')}
                  </th>
                  <th className='text-left px-4 py-2 text-gray-600'>
                    {t('orchestrator.colAction')}
                  </th>
                  <th className='text-left px-4 py-2 text-gray-600'>
                    {t('orchestrator.colMessage')}
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-800/30'>
                {events.map((evt) => (
                  <tr key={evt.id} className='hover:bg-gray-800/20'>
                    <td className='px-4 py-2 text-gray-600 font-mono whitespace-nowrap'>
                      {new Date(evt.created_at).toLocaleTimeString(i18n.language)}
                    </td>
                    <td className='px-4 py-2 text-gray-400 font-mono'>{evt.service ?? '—'}</td>
                    <td className='px-4 py-2'>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          evt.status === 'ok'
                            ? 'bg-green-900/50 text-green-400'
                            : 'bg-red-900/50 text-red-400'
                        }`}
                      >
                        {evt.action}
                      </span>
                    </td>
                    <td className='px-4 py-2 text-gray-400 max-w-xs truncate'>{evt.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Manage Panel ──────────────────────────────────────────────────────────────

function ManagePanel({ env, onDeleted }: { env: OrchestratorEnvStatus; onDeleted: () => void }) {
  const { t } = useTranslation();
  const mcpCall = useOrchCall();
  const qc = useQueryClient();
  const [deleteData, setDeleteData] = useState(false);
  const [keepSpec, setKeepSpec] = useState(true);

  const svcMap = (env as unknown as {
    services?: Record<string, {
      module: string;
      status: string;
      instance: string;
      meta?: { project_id?: string };
    }>;
  }).services ?? {};
  const svcEntries = Object.entries(svcMap);
  const projectId = svcEntries.find(([, s]) => s.meta?.project_id)?.[1]?.meta?.project_id as
    | string
    | undefined;

  const destroyMut = useMutation({
    mutationFn: () =>
      mcpCall('orchestrator.orch_destroy', {
        name: env.name,
        delete_data: deleteData,
        keep_spec: keepSpec,
      }),
    onSuccess: () => {
      toast.success(t('orchestrator.envDestroyed', { env: env.name }));
      onDeleted();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const reapplyMut = useMutation({
    mutationFn: () => mcpCall('orchestrator.orch_apply', { name: env.name, force: true }),
    onSuccess: () => {
      toast.success(t('orchestrator.reapplyStarted'));
      qc.invalidateQueries({ queryKey: ['orch-status'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const exportSpec = () => {
    const spec = (env as unknown as { spec?: unknown }).spec;
    if (!spec) {
      toast.error(t('orchestrator.specNotAvailable'));
      return;
    }
    const blob = new Blob([JSON.stringify(spec, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${env.name}-spec.json`;
    a.click();
    toast.success(t('orchestrator.specExported'));
  };

  const lockMut = useMutation({
    mutationFn: () =>
      mcpCall<{ pin?: string }>('toolbox.production_lock', {
        action: 'set',
        resource_id: `project:${projectId ?? env.name}`,
        reason: t('orchestrator.productionProjectReason', { env: env.name }),
      }),
    onSuccess: (data) => {
      if (data.pin) {
        toast.success(t('orchestrator.lockedWithPin', { pin: data.pin }), { duration: 15000 });
      } else toast.success(t('orchestrator.locked'));
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className='space-y-4'>
      {/* Per-service actions */}
      <Card>
        <CardContent className='p-4'>
          <div className='text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3'>
            {t('orchestrator.servicesTitle')}
          </div>
          <div className='space-y-2'>
            {svcEntries.map(([svcName, svc]) => {
              const ok = svc.status === 'running';
              return (
                <div
                  key={svcName}
                  className='flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-800'
                >
                  <div
                    className={`w-2 h-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-gray-600'}`}
                  />
                  <span className='text-xs text-white font-medium flex-1'>{svcName}</span>
                  <span className='text-[10px] text-gray-600'>{svc.module} · {svc.instance}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Actions grid */}
      <div className='grid grid-cols-2 gap-3'>
        <Card>
          <CardContent className='p-4'>
            <div className='text-xs font-semibold text-blue-400 uppercase tracking-wide mb-2'>
              {t('orchestrator.redeploy')}
            </div>
            <p className='text-[10px] text-gray-500 mb-3'>
              {t('orchestrator.redeployDesc')}
            </p>
            <button
              onClick={() => reapplyMut.mutate()}
              disabled={reapplyMut.isPending}
              className='w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 disabled:opacity-40 text-xs font-medium'
            >
              {reapplyMut.isPending
                ? <Loader2 size={12} className='animate-spin' />
                : <Play size={12} />}
              {t('orchestrator.forceApply')}
            </button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <div className='text-xs font-semibold text-amber-400 uppercase tracking-wide mb-2'>
              {t('orchestrator.backup')}
            </div>
            <p className='text-[10px] text-gray-500 mb-3'>
              {t('orchestrator.exportSpecDesc')}
            </p>
            <button
              onClick={exportSpec}
              className='w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-600/20 border border-amber-500/30 text-amber-400 hover:bg-amber-600/30 text-xs font-medium'
            >
              <Download size={12} /> {t('orchestrator.exportSpec')}
            </button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className='p-4'>
            <div className='text-xs font-semibold text-purple-400 uppercase tracking-wide mb-2'>
              {t('orchestrator.productionLock')}
            </div>
            <p className='text-[10px] text-gray-500 mb-3'>{t('orchestrator.productionLockDesc')}</p>
            <button
              onClick={() => lockMut.mutate()}
              disabled={lockMut.isPending}
              className='w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-purple-600/20 border border-purple-500/30 text-purple-400 hover:bg-purple-600/30 disabled:opacity-40 text-xs font-medium'
            >
              {lockMut.isPending
                ? <Loader2 size={12} className='animate-spin' />
                : <Shield size={12} />}
              {t('orchestrator.lock')}
            </button>
          </CardContent>
        </Card>

        <Card className='border-red-900/30'>
          <CardContent className='p-4'>
            <div className='text-xs font-semibold text-red-400 uppercase tracking-wide mb-2'>
              {t('orchestrator.destroy')}
            </div>
            <div className='space-y-1.5 mb-3'>
              <label className='flex items-center gap-2 cursor-pointer text-[10px] text-gray-400'>
                <input
                  type='checkbox'
                  checked={deleteData}
                  onChange={(e) => setDeleteData(e.target.checked)}
                />{' '}
                {t('orchestrator.deleteData')}
              </label>
              <label className='flex items-center gap-2 cursor-pointer text-[10px] text-gray-400'>
                <input
                  type='checkbox'
                  checked={keepSpec}
                  onChange={(e) => setKeepSpec(e.target.checked)}
                />{' '}
                {t('orchestrator.keepSpec')}
              </label>
            </div>
            <button
              onClick={async () =>
                await themedConfirm(
                  deleteData
                    ? t('orchestrator.confirmDestroyWithData', { env: env.name })
                    : t('orchestrator.confirmDestroy', { env: env.name }),
                ) && destroyMut.mutate()}
              disabled={destroyMut.isPending}
              className='w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/30 disabled:opacity-40 text-xs font-medium'
            >
              {destroyMut.isPending
                ? <Loader2 size={12} className='animate-spin' />
                : <Trash2 size={12} />}
              {destroyMut.isPending ? t('orchestrator.destroying') : t('orchestrator.destroy')}
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Node Select ──────────────────────────────────────────────────────────────

interface NodeOption {
  node: string;
  label: string;
  host: string;
  has_module: boolean;
  cpu_cores: number;
  free_mem_gb: number;
  free_disk_gb: number;
  is_local: boolean;
}

function NodeSelect({ module, value, onChange }: {
  module: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['node-options', module],
    queryFn: () =>
      mcpCall<{ ok: boolean; nodes: NodeOption[] }>(
        'orchestrator.orch_node_options',
        { module },
      ),
    staleTime: 30_000,
  });

  const nodes = data?.nodes ?? [];

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 text-xs text-gray-500 py-2'>
        <Loader2 size={12} className='animate-spin' />{' '}
        {t('orchestrator.searchingServers', { module })}
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className='text-xs text-amber-400 py-2 flex items-center gap-1.5'>
        <AlertCircle size={12} /> {t('orchestrator.noServerWithModule', { module })}
      </div>
    );
  }

  return (
    <div className='space-y-2'>
      <label className='text-xs text-gray-500 font-medium'>{t('orchestrator.server')}</label>
      <div className='grid gap-2'>
        {nodes.map((n) => (
          <div
            key={n.node}
            onClick={() => onChange(n.node === 'local' ? '' : n.node)}
            className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
              (value === '' && n.is_local) || value === n.node
                ? 'border-blue-500/50 bg-blue-500/5'
                : 'border-gray-800 hover:border-gray-600'
            }`}
          >
            <Server size={14} className={n.is_local ? 'text-blue-400' : 'text-gray-500'} />
            <div className='flex-1 min-w-0'>
              <div className='text-sm text-white font-medium truncate'>{n.label}</div>
              <div className='text-[10px] text-gray-600'>{n.host}</div>
            </div>
            <div className='flex items-center gap-3 text-[10px] text-gray-500'>
              <span className='flex items-center gap-1' title={t('orchestrator.cpuCores')}>
                <Cpu size={10} /> {n.cpu_cores}
              </span>
              <span className='flex items-center gap-1' title={t('orchestrator.freeRam')}>
                <MemoryStick size={10} /> {n.free_mem_gb} GB
              </span>
              <span className='flex items-center gap-1' title={t('orchestrator.freeDisk')}>
                <HardDrive size={10} /> {n.free_disk_gb} GB
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Project Templates ────────────────────────────────────────────────────────

interface ProjectTemplate {
  id: string;
  label: string;
  descKey: string;
  Icon: typeof Rocket;
  iconColor: string;
  borderColor: string;
  defaults: {
    backendType: 'supabase' | 'postgres' | 'mariadb' | 'none';
    frontendType: 'react' | 'node' | 'deno' | 'laravel' | 'none';
    backupEnabled: boolean;
    production: boolean;
    buildCmd?: string;
    buildOutput?: string;
  };
}

const TEMPLATES: ProjectTemplate[] = [
  {
    id: 'lovable',
    label: 'Lovable Stack',
    descKey: 'tplLovableDesc',
    Icon: Rocket,
    iconColor: 'text-pink-400',
    borderColor: 'border-pink-500/30 hover:border-pink-500/50',
    defaults: {
      backendType: 'supabase',
      frontendType: 'react',
      backupEnabled: true,
      production: false,
      buildCmd: 'npm ci && npm run build',
      buildOutput: 'dist',
    },
  },
  {
    id: 'node-api',
    label: 'Node.js API',
    descKey: 'tplNodeApiDesc',
    Icon: Server,
    iconColor: 'text-green-400',
    borderColor: 'border-green-500/30 hover:border-green-500/50',
    defaults: {
      backendType: 'postgres',
      frontendType: 'node',
      backupEnabled: true,
      production: false,
    },
  },
  {
    id: 'deno-supabase',
    label: 'Deno + Supabase',
    descKey: 'tplDenoSupabaseDesc',
    Icon: Zap,
    iconColor: 'text-cyan-400',
    borderColor: 'border-cyan-500/30 hover:border-cyan-500/50',
    defaults: {
      backendType: 'supabase',
      frontendType: 'deno',
      backupEnabled: true,
      production: false,
    },
  },
  {
    id: 'static',
    label: 'Static Web',
    descKey: 'tplStaticDesc',
    Icon: Globe,
    iconColor: 'text-amber-400',
    borderColor: 'border-amber-500/30 hover:border-amber-500/50',
    defaults: {
      backendType: 'none',
      frontendType: 'react',
      backupEnabled: false,
      production: false,
      buildCmd: 'npm ci && npm run build',
      buildOutput: 'dist',
    },
  },
  {
    id: 'backend-only',
    label: 'Backend API',
    descKey: 'tplBackendOnlyDesc',
    Icon: Database,
    iconColor: 'text-blue-400',
    borderColor: 'border-blue-500/30 hover:border-blue-500/50',
    defaults: {
      backendType: 'supabase',
      frontendType: 'none',
      backupEnabled: true,
      production: false,
    },
  },
  {
    id: 'custom',
    label: 'Custom',
    descKey: 'tplCustomDesc',
    Icon: Layout,
    iconColor: 'text-gray-400',
    borderColor: 'border-gray-600 hover:border-gray-500',
    defaults: {
      backendType: 'none',
      frontendType: 'none',
      backupEnabled: false,
      production: false,
    },
  },
];

// ── Project Wizard ───────────────────────────────────────────────────────────

type WizardStep =
  | 'template'
  | 'name'
  | 'backend'
  | 'frontend'
  | 'domain'
  | 'backup'
  | 'review'
  | 'deploying';
const STEPS: WizardStep[] = [
  'template',
  'name',
  'backend',
  'frontend',
  'domain',
  'backup',
  'review',
];
const STEP_LABELS: Record<WizardStep, string> = {
  template: 'stepTemplate',
  name: 'stepName',
  backend: 'stepBackend',
  frontend: 'stepFrontend',
  domain: 'stepDomain',
  backup: 'stepBackup',
  review: 'stepReview',
  deploying: 'stepDeploy',
};

interface DeployProgress {
  phase: 'wizard' | 'define' | 'apply' | 'publish' | 'lock' | 'done' | 'error';
  projectId?: string;
  services: Record<string, { status: 'pending' | 'running' | 'done' | 'error'; detail?: string }>;
  error?: string;
}

const FRONTEND_TYPES = [
  { id: 'react' as const, label: 'React', descKey: 'feReactDesc', Icon: Layout },
  { id: 'node' as const, label: 'Node.js', descKey: 'feNodeDesc', Icon: Server },
  { id: 'deno' as const, label: 'Deno', descKey: 'feDenoDesc', Icon: Zap },
  { id: 'laravel' as const, label: 'Laravel', descKey: 'feLaravelDesc', Icon: Globe },
];

function CreateEnvForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState<WizardStep>('template');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [backendType, setBackendType] = useState<'supabase' | 'postgres' | 'mariadb' | 'none'>(
    'supabase',
  );
  const [frontendType, setFrontendType] = useState<'react' | 'node' | 'deno' | 'laravel' | 'none'>(
    'react',
  );
  const [gitRepo, setGitRepo] = useState('');
  const [gitBranch, setGitBranch] = useState('main');
  const [gitToken, setGitToken] = useState('');
  const [domain, setDomain] = useState('');
  const [backupEnabled, setBackupEnabled] = useState(true);
  const [backupSchedule, setBackupSchedule] = useState('0 3 * * *');
  const [production, setProduction] = useState(false);
  const [buildCmd, setBuildCmd] = useState('npm ci && npm run build');
  const [buildOutput, setBuildOutput] = useState('dist');
  // Per-service node placement
  const [backendNode, setBackendNode] = useState('');
  const [frontendNode, setFrontendNode] = useState('');
  // Deploy progress
  const [progress, setProgress] = useState<DeployProgress | null>(null);

  const stepIdx = STEPS.indexOf(step);
  const canNext = step === 'template' ? !!templateId : step === 'name' ? name.length >= 2 : true;

  const applyTemplate = (tpl: ProjectTemplate) => {
    setTemplateId(tpl.id);
    setBackendType(tpl.defaults.backendType);
    setFrontendType(tpl.defaults.frontendType);
    setBackupEnabled(tpl.defaults.backupEnabled);
    setProduction(tpl.defaults.production);
    if (tpl.defaults.buildCmd) setBuildCmd(tpl.defaults.buildCmd);
    if (tpl.defaults.buildOutput) setBuildOutput(tpl.defaults.buildOutput);
  };

  // Spec NIKDY neskládá UI — skládá ho orchestrátor (orch_wizard MCP tool);
  // UI jen mapuje formulář 1:1 na argumenty a výsledek zobrazuje.
  // (Filip 2026-07-17: v UI žádná vkládaná logika, jen standardní MCP volání.)
  const [wizardSpec, setWizardSpec] = useState<
    { services: Record<string, unknown>; topology?: { target?: string } } | null
  >(null);
  const [specError, setSpecError] = useState<string | null>(null);
  const fetchSpec = async () => {
    setSpecError(null);
    setWizardSpec(null);
    try {
      const r = await mcpCall<{
        ok: boolean;
        error?: string;
        spec?: { services: Record<string, unknown>; topology?: { target?: string } };
      }>('orchestrator.orch_wizard', {
        mode: 'manual',
        name,
        frontend: frontendType,
        backend: backendType,
        ...(domain ? { domain } : {}),
        ...(gitRepo ? { git_repo: gitRepo, git_branch: gitBranch } : {}),
        ...(gitRepo && gitToken ? { git_token: gitToken } : {}),
        ...(gitRepo ? { build_cmd: buildCmd, build_output: buildOutput } : {}),
        ...(backupEnabled ? { backup_schedule: backupSchedule } : {}),
        production,
        ...(backendNode ? { backend_node: backendNode } : {}),
        ...(frontendNode ? { frontend_node: frontendNode } : {}),
      });
      if (!r.ok || !r.spec) {
        setSpecError(r.error ?? t('orchestrator.wizardToolFailed'));
        return null;
      }
      setWizardSpec(r.spec);
      return r.spec;
    } catch (e) {
      setSpecError(String((e as Error).message ?? e));
      return null;
    }
  };
  // Vstup na review = načti spec z orchestrátoru (validace repa/tokenu proběhne tam)
  useEffect(() => {
    if (step === 'review') void fetchSpec();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  const buildSpec = () => wizardSpec ?? { services: {} as Record<string, unknown> };

  // Poll orchestrator status to update progress in real-time
  const pollStatus = (envName: string, svcNames: string[], signal: AbortSignal) => {
    const poll = async () => {
      while (!signal.aborted) {
        await new Promise((r) => setTimeout(r, 3000));
        if (signal.aborted) break;
        try {
          const st = await mcpCall<{
            ok: boolean;
            status?: string;
            state?: {
              services?: Array<{ name: string; status: string; module: string; error?: string }>;
            };
          }>('orchestrator.orch_status', { name: envName });
          if (!st.ok) continue;
          const svcs = st.state?.services ?? [];
          if (svcs.length > 0) {
            setProgress((p) => {
              if (!p || p.phase === 'done' || p.phase === 'error') return p;
              const updated = { ...p.services };
              for (const svc of svcs) {
                updated[svc.name] = {
                  status: svc.status === 'running'
                    ? 'done'
                    : svc.status === 'error'
                    ? 'error'
                    : 'running',
                  detail: svc.error,
                };
              }
              // Services not yet in state → still running
              for (const s of svcNames) {
                if (!updated[s] || updated[s].status === 'pending') {
                  updated[s] = { status: 'running' };
                }
              }
              return { ...p, services: updated };
            });
          }
        } catch { /* poll error — ignore */ }
      }
    };
    poll();
  };

  const startDeploy = async () => {
    // Spec VZDY z orchestratoru (orch_wizard) — UI nic neskladá.
    setStep('deploying');
    setProgress({ phase: 'wizard', services: {} });
    const spec = wizardSpec ?? await fetchSpec();
    if (!spec || !Object.keys(spec.services).length) {
      setProgress({
        phase: 'error',
        services: {},
        error: specError ?? t('orchestrator.wizardFailed'),
      });
      return;
    }
    const svcNames = Object.keys(spec.services);

    // Init progress — all services pending
    const initSvcs: DeployProgress['services'] = {};
    for (const s of svcNames) initSvcs[s] = { status: 'pending' };
    setProgress({ phase: 'define', services: initSvcs });
    setStep('deploying');

    try {
      // 1. Define (quick — standard timeout OK)
      const defRes = await mcpCall<{ ok: boolean; project_id?: string; error?: string }>(
        'orchestrator.orch_define',
        { name, spec },
      );
      if (!defRes.ok) throw new Error(defRes.error ?? t('orchestrator.defineFailed'));
      setProgress((p) => p ? { ...p, phase: 'apply', projectId: defRes.project_id } : p);

      // 2. Apply — long running, start polling status in parallel
      const runningSvcs: DeployProgress['services'] = {};
      for (const s of svcNames) runningSvcs[s] = { status: 'running' };
      setProgress((p) => p ? { ...p, services: runningSvcs } : p);

      const pollController = new AbortController();
      pollStatus(name, svcNames, pollController.signal);

      try {
        const applyRes = await mcpCall<{
          ok: boolean;
          status?: string;
          error?: string;
          log?: string[];
          results?: Array<{ service: string; ok: boolean; detail?: string }>;
        }>('orchestrator.orch_apply', { name });

        pollController.abort(); // stop polling

        // Final per-service status from apply results
        const finalSvcs: DeployProgress['services'] = {};
        if (applyRes.results) {
          for (const r of applyRes.results) {
            finalSvcs[r.service] = { status: r.ok ? 'done' : 'error', detail: r.detail };
          }
        }
        for (const s of svcNames) {
          if (!finalSvcs[s]) finalSvcs[s] = { status: applyRes.ok ? 'done' : 'pending' };
        }
        const hasErrors = Object.values(finalSvcs).some((s) => s.status === 'error');
        const allDone = Object.values(finalSvcs).every((s) => s.status === 'done');
        const resultPhase = allDone ? 'done' : hasErrors ? 'error' : 'done';
        setProgress((p) =>
          p
            ? {
              ...p,
              services: finalSvcs,
              phase: resultPhase,
              error: hasErrors
                ? t('orchestrator.servicesFailed', {
                  count: Object.values(finalSvcs).filter((s) => s.status === 'error').length,
                })
                : undefined,
            }
            : p
        );
      } catch (e) {
        pollController.abort();
        throw e;
      }

      // 3. Publikace domény — VÝHRADNĚ standard flow (orch_topology_set →
      //    network_publish: edge NPM + LE cert + catalog). Žádný lokální nginx.
      const topo = (spec as { topology?: { target?: string } }).topology;
      const anySvcError = Object.values(progress?.services ?? {}).some((s) => s.status === 'error');
      if (topo?.target && !anySvcError) {
        setProgress((p) => p ? { ...p, phase: 'publish' } : p);
        try {
          const pubR = await mcpCall<{
            ok: boolean;
            error?: string;
            steps?: Array<{ step: string; ok: boolean; detail: string }>;
          }>('orchestrator.orch_topology_set', { name, target: topo.target, apply: true });
          if (!pubR.ok) {
            const failed = (pubR.steps ?? []).filter((s) => !s.ok)
              .map((s) => `${s.step}: ${s.detail}`).join('; ');
            throw new Error(failed || pubR.error || t('orchestrator.publishStepFailed'));
          }
        } catch (e) {
          setProgress((p) =>
            p
              ? {
                ...p,
                phase: 'error',
                error: `${t('orchestrator.publishFailed')}: ${String((e as Error).message)}`,
              }
              : p
          );
          return;
        }
        setProgress((p) => p && p.phase === 'publish' ? { ...p, phase: 'done' } : p);
      }

      // 4. Production lock (only if no errors)
      const hasAnyError = Object.values(progress?.services ?? {}).some((s) => s.status === 'error');
      if (production && !hasAnyError) {
        setProgress((p) => p ? { ...p, phase: 'lock' } : p);
        try {
          const lockData = await mcpCall<{ pin?: string }>('toolbox.production_lock', {
            action: 'set',
            resource_id: `project:${defRes.project_id ?? name}`,
            reason: t('orchestrator.productionProjectReason', { env: name }),
          });
          if (lockData.pin) {
            toast.success(t('orchestrator.lockedWithPin', { pin: lockData.pin }), {
              duration: 15000,
            });
          }
        } catch { /* non-fatal */ }
      }

      // 4. Final phase already set in apply results above
    } catch (e) {
      setProgress((p) => p ? { ...p, phase: 'error', error: (e as Error).message } : p);
    }
  };

  const inp =
    'w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-colors';
  const optCard = (selected: boolean, extra = '') =>
    `relative p-4 rounded-xl border-2 cursor-pointer transition-all ${extra} ${
      selected
        ? 'border-blue-500 bg-blue-500/5 shadow-lg shadow-blue-500/5'
        : 'border-gray-800 bg-gray-900/30 hover:bg-gray-900/50'
    }`;

  return (
    <div
      className='fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-gray-900 border border-gray-700/50 rounded-2xl w-full max-w-3xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with step labels */}
        <div className='px-6 pt-5 pb-4 border-b border-gray-800/50'>
          <div className='flex items-center gap-1.5'>
            {STEPS.map((s, i) => {
              const active = i === stepIdx;
              const done = i < stepIdx;
              return (
                <div key={s} className='flex items-center gap-1.5 flex-1'>
                  <div
                    className={`flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold transition-colors ${
                      active
                        ? 'bg-blue-500 text-white'
                        : done
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-gray-800 text-gray-600'
                    }`}
                  >
                    {done ? <CheckCircle2 size={12} /> : i + 1}
                  </div>
                  <span
                    className={`text-[10px] font-medium hidden sm:block ${
                      active ? 'text-white' : done ? 'text-blue-400' : 'text-gray-600'
                    }`}
                  >
                    {t(`orchestrator.${STEP_LABELS[s]}`)}
                  </span>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-px ${done ? 'bg-blue-500/30' : 'bg-gray-800'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className='flex-1 overflow-y-auto px-6 py-5 space-y-5'>
          {/* ── Step: Template ── */}
          {step === 'template' && (
            <>
              <div>
                <h2 className='text-lg font-bold text-white mb-1'>
                  {t('orchestrator.newProject')}
                </h2>
                <p className='text-sm text-gray-500'>{t('orchestrator.newProjectDesc')}</p>
              </div>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                {TEMPLATES.map((tpl) => (
                  <div
                    key={tpl.id}
                    onClick={() => applyTemplate(tpl)}
                    className={`${optCard(templateId === tpl.id, tpl.borderColor)} group`}
                  >
                    <div className='flex items-start gap-3'>
                      <div className={`mt-0.5 ${tpl.iconColor}`}>
                        <tpl.Icon size={20} />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='text-sm font-semibold text-white'>{tpl.label}</div>
                        <div className='text-xs text-gray-500 mt-1 leading-relaxed'>
                          {t(`orchestrator.${tpl.descKey}`)}
                        </div>
                      </div>
                    </div>
                    {templateId === tpl.id && (
                      <div className='absolute top-2 right-2'>
                        <CheckCircle2 size={16} className='text-blue-400' />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Step: Name ── */}
          {step === 'name' && (
            <>
              <div>
                <h2 className='text-lg font-bold text-white mb-1'>
                  {t('orchestrator.projectName')}
                </h2>
                <p className='text-sm text-gray-500'>
                  {t('orchestrator.projectNameDesc')}
                </p>
              </div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                placeholder='lovable-demo'
                className={inp + ' text-base'}
                autoFocus
              />
              {name && (
                <div className='text-xs text-gray-500 bg-gray-950/50 rounded-lg p-3 border border-gray-800'>
                  <div className='flex gap-2'>
                    <span className='text-gray-600 w-28'>{t('orchestrator.instancePrefix')}</span>
                    <span className='font-mono text-gray-400'>{name}-*</span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Step: Backend ── */}
          {step === 'backend' && (
            <>
              <div>
                <h2 className='text-lg font-bold text-white mb-1'>
                  {t('orchestrator.stepBackendTitle')}
                </h2>
                <p className='text-sm text-gray-500'>{t('orchestrator.backendStepDesc')}</p>
              </div>
              <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
                {[
                  {
                    id: 'supabase' as const,
                    label: 'Supabase',
                    desc: t('orchestrator.backendSupabaseDesc'),
                    Icon: Database,
                    color: 'text-purple-400',
                  },
                  {
                    id: 'postgres' as const,
                    label: 'PostgreSQL',
                    desc: t('orchestrator.backendPostgresDesc'),
                    Icon: Database,
                    color: 'text-blue-400',
                  },
                  {
                    id: 'mariadb' as const,
                    label: 'MariaDB',
                    desc: t('orchestrator.backendMariadbDesc'),
                    Icon: Database,
                    color: 'text-orange-400',
                  },
                  {
                    id: 'none' as const,
                    label: t('orchestrator.none'),
                    desc: t('orchestrator.backendNoneDesc'),
                    Icon: Globe,
                    color: 'text-gray-400',
                  },
                ].map((opt) => (
                  <div
                    key={opt.id}
                    className={optCard(backendType === opt.id)}
                    onClick={() => setBackendType(opt.id)}
                  >
                    <opt.Icon size={18} className={`mb-2 ${opt.color}`} />
                    <div className='text-sm font-semibold text-white'>{opt.label}</div>
                    <div className='text-[10px] text-gray-500 mt-1 leading-relaxed'>{opt.desc}</div>
                  </div>
                ))}
              </div>
              {backendType !== 'none' && (
                <NodeSelect
                  module={backendType === 'supabase' ? 'supabase' : 'dbhost'}
                  value={backendNode}
                  onChange={setBackendNode}
                />
              )}
            </>
          )}

          {/* ── Step: Frontend ── */}
          {step === 'frontend' && (
            <>
              <div>
                <h2 className='text-lg font-bold text-white mb-1'>
                  {t('orchestrator.stepFrontendTitle')}
                </h2>
                <p className='text-sm text-gray-500'>{t('orchestrator.frontendStepDesc')}</p>
              </div>
              <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
                {FRONTEND_TYPES.map((ft) => (
                  <div
                    key={ft.id}
                    className={optCard(frontendType === ft.id)}
                    onClick={() => setFrontendType(ft.id)}
                  >
                    <ft.Icon size={18} className='mb-2 text-gray-400' />
                    <div className='text-sm font-semibold text-white'>{ft.label}</div>
                    <div className='text-[10px] text-gray-500 mt-1 leading-relaxed'>
                      {t(`orchestrator.${ft.descKey}`)}
                    </div>
                  </div>
                ))}
                <div
                  className={optCard(frontendType === 'none')}
                  onClick={() => setFrontendType('none')}
                >
                  <Globe size={18} className='mb-2 text-gray-600' />
                  <div className='text-sm font-semibold text-white'>
                    {t('orchestrator.externalOrNone')}
                  </div>
                  <div className='text-[10px] text-gray-500 mt-1'>
                    {t('orchestrator.externalOrNoneDesc')}
                  </div>
                </div>
              </div>

              {frontendType !== 'none' && (
                <>
                  <div className='space-y-3 pt-2'>
                    <div>
                      <label className='text-xs text-gray-500 font-medium mb-1 block'>
                        {t('orchestrator.gitRepository')}
                      </label>
                      <div className='flex items-center gap-2'>
                        <GitBranch size={14} className='text-gray-600 flex-shrink-0' />
                        <input
                          value={gitRepo}
                          onChange={(e) => setGitRepo(e.target.value)}
                          placeholder='https://gitlab.solanaenterprise.cz/antivandal/my-app.git'
                          className={inp}
                        />
                      </div>
                    </div>
                    <div className='grid grid-cols-2 gap-3'>
                      <div>
                        <label className='text-xs text-gray-500 font-medium mb-1 block'>
                          Branch
                        </label>
                        <input
                          value={gitBranch}
                          onChange={(e) => setGitBranch(e.target.value)}
                          placeholder='main'
                          className={inp}
                        />
                      </div>
                      <div>
                        <label className='text-xs text-gray-500 font-medium mb-1 block'>
                          {t('orchestrator.tokenOptional')}
                        </label>
                        <input
                          value={gitToken}
                          onChange={(e) => setGitToken(e.target.value)}
                          placeholder='glpat-...'
                          className={inp}
                          type='password'
                        />
                      </div>
                    </div>
                    {['react', 'deno'].includes(frontendType) && (
                      <div className='grid grid-cols-2 gap-3'>
                        <div>
                          <label className='text-xs text-gray-500 font-medium mb-1 block'>
                            Build command
                          </label>
                          <input
                            value={buildCmd}
                            onChange={(e) => setBuildCmd(e.target.value)}
                            placeholder='npm ci && npm run build'
                            className={inp + ' font-mono text-xs'}
                          />
                        </div>
                        <div>
                          <label className='text-xs text-gray-500 font-medium mb-1 block'>
                            Build output
                          </label>
                          <input
                            value={buildOutput}
                            onChange={(e) => setBuildOutput(e.target.value)}
                            placeholder='dist'
                            className={inp + ' font-mono text-xs'}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <NodeSelect module='apphost' value={frontendNode} onChange={setFrontendNode} />
                </>
              )}
            </>
          )}

          {/* ── Step: Domain ── */}
          {step === 'domain' && (
            <>
              <div>
                <h2 className='text-lg font-bold text-white mb-1'>
                  {t('orchestrator.publicDomain')}
                </h2>
                <p className='text-sm text-gray-500'>
                  {frontendType === 'none' && backendType !== 'none'
                    ? t('orchestrator.domainApiDesc')
                    : t('orchestrator.domainProxyDesc')}
                </p>
              </div>
              <div className='flex items-center gap-2'>
                <Globe size={14} className='text-gray-600 flex-shrink-0' />
                <input
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder={`${
                    name || t('orchestrator.projectPlaceholder')
                  }.services.maxcloud.cz`}
                  className={inp}
                  autoFocus
                />
              </div>
              {domain && (
                <div className='text-xs text-gray-500 bg-gray-950/50 rounded-lg p-3 border border-gray-800 space-y-1'>
                  <div className='flex gap-2'>
                    <span className='text-gray-600 w-24'>{t('orchestrator.httpsLabel')}:</span>
                    <span className='font-mono text-green-400'>https://{domain}</span>
                  </div>
                  <div className='flex gap-2'>
                    <span className='text-gray-600 w-24'>{t('orchestrator.certificate')}</span>
                    <span className='text-gray-400'>{t('orchestrator.letsEncryptAuto')}</span>
                  </div>
                  <div className='flex gap-2'>
                    <span className='text-gray-600 w-24'>{t('orchestrator.proxyTo')}</span>
                    <span className='text-gray-400'>
                      {frontendType !== 'none'
                        ? 'frontend (AppHost)'
                        : backendType === 'supabase'
                        ? 'Supabase API'
                        : 'PostgreSQL'}
                    </span>
                  </div>
                </div>
              )}
              {frontendType === 'none' && backendType !== 'none' && !domain && (
                <div className='text-xs text-gray-500 bg-gray-800/30 rounded-lg p-3 border border-gray-800'>
                  <span className='text-gray-400'>
                    {t('orchestrator.noDomainNote')}
                  </span>
                  {backendType === 'supabase' && (
                    <span className='text-gray-500 block mt-1'>
                      {t('orchestrator.noDomainSupabaseNote')}
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Step: Backup ── */}
          {step === 'backup' && (
            <>
              <div>
                <h2 className='text-lg font-bold text-white mb-1'>
                  {t('orchestrator.backupProtection')}
                </h2>
                <p className='text-sm text-gray-500'>{t('orchestrator.backupProtectionDesc')}</p>
              </div>
              <div
                onClick={() => setBackupEnabled(!backupEnabled)}
                className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  backupEnabled ? 'border-blue-500/30 bg-blue-500/5' : 'border-gray-800'
                }`}
              >
                <Archive size={20} className={backupEnabled ? 'text-blue-400' : 'text-gray-600'} />
                <div className='flex-1'>
                  <div className='text-sm font-semibold text-white'>
                    {t('orchestrator.automaticBackup')}
                  </div>
                  <div className='text-xs text-gray-500'>
                    {t('orchestrator.automaticBackupDesc')}
                  </div>
                </div>
                <div
                  className={`w-10 h-5 rounded-full transition-colors flex items-center ${
                    backupEnabled ? 'bg-blue-500 justify-end' : 'bg-gray-700 justify-start'
                  }`}
                >
                  <div className='w-4 h-4 bg-white rounded-full mx-0.5 shadow' />
                </div>
              </div>
              {backupEnabled && (
                <div>
                  <label className='text-xs text-gray-500 font-medium mb-1 block'>
                    {t('orchestrator.scheduleCron')}
                  </label>
                  <input
                    value={backupSchedule}
                    onChange={(e) => setBackupSchedule(e.target.value)}
                    placeholder='0 3 * * *'
                    className={inp + ' font-mono'}
                  />
                </div>
              )}
              <div
                onClick={() => setProduction(!production)}
                className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  production ? 'border-amber-500/30 bg-amber-500/5' : 'border-gray-800'
                }`}
              >
                <Shield size={20} className={production ? 'text-amber-400' : 'text-gray-600'} />
                <div className='flex-1'>
                  <div className='text-sm font-semibold text-white'>
                    {t('orchestrator.productionEnv')}
                  </div>
                  <div className='text-xs text-gray-500'>
                    {t('orchestrator.productionEnvDesc')}
                  </div>
                </div>
                <div
                  className={`w-10 h-5 rounded-full transition-colors flex items-center ${
                    production ? 'bg-amber-500 justify-end' : 'bg-gray-700 justify-start'
                  }`}
                >
                  <div className='w-4 h-4 bg-white rounded-full mx-0.5 shadow' />
                </div>
              </div>
            </>
          )}

          {/* ── Step: Review ── */}
          {step === 'review' && (
            <>
              <div>
                <h2 className='text-lg font-bold text-white mb-1'>
                  {t('orchestrator.summary')} — {name}
                </h2>
                <p className='text-sm text-gray-500'>{t('orchestrator.summaryDesc')}</p>
              </div>
              <div className='space-y-1'>
                {([
                  {
                    key: 'template',
                    label: t('orchestrator.rowTemplate'),
                    value: TEMPLATES.find((tpl) => tpl.id === templateId)?.label ?? 'Custom',
                    Icon: Layout,
                  },
                  {
                    key: 'backend',
                    label: 'Backend',
                    value: backendType === 'none'
                      ? '—'
                      : backendType + (backendNode ? ` @ ${backendNode}` : ' @ local'),
                    Icon: Database,
                  },
                  {
                    key: 'frontend',
                    label: 'Frontend',
                    value: frontendType === 'none'
                      ? '—'
                      : frontendType + (frontendNode ? ` @ ${frontendNode}` : ' @ local'),
                    Icon: Server,
                  },
                  {
                    key: 'git',
                    label: 'Git',
                    value: gitRepo ? `${gitRepo.split('/').pop()} (${gitBranch})` : '—',
                    Icon: GitBranch,
                  },
                  {
                    key: 'domain',
                    label: t('orchestrator.rowDomain'),
                    value: domain || t('orchestrator.internal'),
                    Icon: Globe,
                  },
                  {
                    key: 'backup',
                    label: 'Backup',
                    value: backupEnabled ? backupSchedule : '—',
                    Icon: Archive,
                  },
                  {
                    key: 'production',
                    label: t('orchestrator.rowProduction'),
                    value: production ? t('orchestrator.yesLockPin') : t('orchestrator.no'),
                    Icon: Shield,
                  },
                ] as const).map((row) => (
                  <div
                    key={row.key}
                    className='flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-gray-800/30'
                  >
                    <row.Icon size={14} className='text-gray-600 flex-shrink-0' />
                    <span className='text-sm text-gray-500 w-24 flex-shrink-0'>{row.label}</span>
                    <span
                      className={`text-sm font-medium ${
                        row.key === 'production' && production ? 'text-amber-400' : 'text-white'
                      }`}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>

              {specError && (
                <div className='bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-400'>
                  {specError}
                </div>
              )}
              {!wizardSpec && !specError && (
                <div className='flex items-center gap-2 text-sm text-gray-400'>
                  <Loader2 size={14} className='animate-spin' />
                  {t('orchestrator.generatingSpec')}
                </div>
              )}
              <div className='bg-gray-950/50 rounded-xl border border-gray-800 p-4'>
                <StackFlowGraphic
                  title={t('orchestrator.servicesToCreate')}
                  services={stackFromWizardSpec(buildSpec())}
                />
              </div>

              {/* Spec JSON + akce */}
              <div className='bg-gray-950/50 rounded-xl border border-gray-800 p-4'>
                <div className='flex items-center justify-between mb-3'>
                  <span className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                    Spec JSON
                  </span>
                  <div className='flex gap-2'>
                    <button
                      onClick={() => {
                        const blob = new Blob([JSON.stringify(buildSpec(), null, 2)], {
                          type: 'application/json',
                        });
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(blob);
                        a.download = `${name}-spec.json`;
                        a.click();
                      }}
                      className='flex items-center gap-1.5 px-2.5 py-1 text-[10px] rounded border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors'
                    >
                      <Download size={10} /> {t('orchestrator.downloadJson')}
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(buildSpec(), null, 2));
                        toast.success(t('orchestrator.specCopied'));
                      }}
                      className='flex items-center gap-1.5 px-2.5 py-1 text-[10px] rounded border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors'
                    >
                      {t('orchestrator.copy')}
                    </button>
                  </div>
                </div>
                <pre className='text-[10px] font-mono text-gray-500 overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap'>
                  {JSON.stringify(buildSpec(), null, 2)}
                </pre>
              </div>
            </>
          )}

          {/* ── Step: Deploying (progress screen) ── */}
          {step === 'deploying' && progress && (
            <>
              <div className='text-center mb-2'>
                {progress.phase === 'done'
                  ? <CheckCircle2 size={40} className='text-green-400 mx-auto mb-3' />
                  : progress.phase === 'error'
                  ? <AlertCircle size={40} className='text-red-400 mx-auto mb-3' />
                  : <Loader2 size={40} className='text-blue-400 mx-auto mb-3 animate-spin' />}
                <h2 className='text-lg font-bold text-white'>
                  {progress.phase === 'done'
                    ? t('orchestrator.projectDeployed', { name })
                    : progress.phase === 'error'
                    ? t('orchestrator.deployFailedTitle')
                    : progress.phase === 'wizard'
                    ? t('orchestrator.generatingSpec')
                    : progress.phase === 'publish'
                    ? t('orchestrator.publishingDomain')
                    : progress.phase === 'define'
                    ? t('orchestrator.creatingProject')
                    : progress.phase === 'lock'
                    ? t('orchestrator.lockingProject')
                    : t('orchestrator.deployingServices')}
                </h2>
                {progress.projectId && (
                  <span className='text-xs font-mono text-gray-500 mt-1 block'>
                    {progress.projectId}
                  </span>
                )}
              </div>

              {progress.error && (
                <div className='bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-400'>
                  {progress.error}
                </div>
              )}

              <div className='space-y-2'>
                {Object.entries(progress.services).map(([svcName, svc]) => {
                  const svcSpec = buildSpec().services[svcName] as
                    | Record<string, unknown>
                    | undefined;
                  const moduleName = (svcSpec?.module ?? 'unknown') as string;
                  const Icon = SVC_ICONS[moduleName] ?? Server;
                  // Build tech detail string
                  const details: string[] = [];
                  if (moduleName === 'supabase') {
                    details.push('PostgreSQL + REST API + Auth + Studio');
                  } else if (moduleName === 'dbhost') {
                    details.push(`${svcSpec?.engine ?? 'postgres'} ${svcSpec?.version ?? ''}`);
                  } else if (moduleName === 'apphost') {
                    const appType = svcSpec?.type as string;
                    details.push(
                      appType === 'react'
                        ? 'React + Vite'
                        : appType === 'deno'
                        ? 'Deno'
                        : appType === 'node'
                        ? 'Node.js'
                        : appType === 'laravel'
                        ? 'Laravel + PHP'
                        : appType ?? '',
                    );
                    if (svcSpec?.git_repo) {
                      details.push(
                        (svcSpec.git_repo as string).split('/').pop()?.replace('.git', '') ?? '',
                      );
                    }
                  } else if (moduleName === 'npm') details.push(svcSpec?.domain as string ?? '');
                  else if (moduleName === 'backup') details.push(svcSpec?.schedule as string ?? '');
                  const targetNode = svcSpec?.target_node as string | undefined;
                  return (
                    <div
                      key={svcName}
                      className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all ${
                        svc.status === 'done'
                          ? 'border-green-500/20 bg-green-500/5'
                          : svc.status === 'error'
                          ? 'border-red-500/20 bg-red-500/5'
                          : svc.status === 'running'
                          ? 'border-blue-500/20 bg-blue-500/5'
                          : 'border-gray-800 bg-gray-900/20'
                      }`}
                    >
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          svc.status === 'done'
                            ? 'bg-green-500/10'
                            : svc.status === 'error'
                            ? 'bg-red-500/10'
                            : svc.status === 'running'
                            ? 'bg-blue-500/10'
                            : 'bg-gray-800/50'
                        }`}
                      >
                        {svc.status === 'running'
                          ? <Loader2 size={18} className='text-blue-400 animate-spin' />
                          : svc.status === 'done'
                          ? <CheckCircle2 size={18} className='text-green-400' />
                          : svc.status === 'error'
                          ? <XCircle size={18} className='text-red-400' />
                          : (
                            <Icon size={18} className={SVC_COLORS[moduleName] ?? 'text-gray-600'} />
                          )}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2'>
                          <span className='text-sm font-semibold text-white'>{svcName}</span>
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              SVC_COLORS[moduleName] ?? 'text-gray-500'
                            } bg-gray-800/50`}
                          >
                            {moduleName}
                          </span>
                        </div>
                        <div className='flex items-center gap-2 mt-0.5 flex-wrap'>
                          {details.filter(Boolean).map((d, i) => (
                            <span key={i} className='text-[11px] text-gray-500'>{d}</span>
                          ))}
                          {targetNode && (
                            <span className='text-[10px] font-mono text-gray-600 bg-gray-800 px-1.5 py-0.5 rounded flex items-center gap-1'>
                              <Server size={8} /> {targetNode}
                            </span>
                          )}
                          {!targetNode && (
                            <span className='text-[10px] font-mono text-gray-600 bg-gray-800 px-1.5 py-0.5 rounded flex items-center gap-1'>
                              <Server size={8} /> local
                            </span>
                          )}
                        </div>
                        {svc.detail && (
                          <div
                            className={`text-xs mt-1 ${
                              svc.status === 'error' ? 'text-red-400' : 'text-gray-500'
                            }`}
                          >
                            {svc.detail}
                          </div>
                        )}
                      </div>
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wider ${
                          svc.status === 'done'
                            ? 'text-green-400'
                            : svc.status === 'error'
                            ? 'text-red-400'
                            : svc.status === 'running'
                            ? 'text-blue-400'
                            : 'text-gray-600'
                        }`}
                      >
                        {svc.status === 'done'
                          ? 'OK'
                          : svc.status === 'error'
                          ? t('orchestrator.progressError')
                          : svc.status === 'running'
                          ? t('orchestrator.progressRunning')
                          : t('orchestrator.progressWaiting')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer nav */}
        <div className='flex items-center gap-3 px-6 py-4 border-t border-gray-800/50 bg-gray-900/80'>
          {step === 'deploying'
            ? (
              <>
                {(progress?.phase === 'done' || progress?.phase === 'error') && (
                  <button
                    onClick={() => {
                      onCreated();
                    }}
                    className='ml-auto flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm font-medium text-white transition-colors'
                  >
                    {progress?.phase === 'done'
                      ? t('orchestrator.showProject')
                      : t('orchestrator.close')}
                  </button>
                )}
              </>
            )
            : (
              <>
                <button
                  onClick={onClose}
                  className='px-4 py-2.5 rounded-lg border border-gray-700 text-sm text-gray-400 hover:text-white hover:border-gray-500 transition-colors'
                >
                  {t('orchestrator.cancel')}
                </button>
                {stepIdx > 0 && (
                  <button
                    onClick={() => setStep(STEPS[stepIdx - 1])}
                    className='px-4 py-2.5 rounded-lg border border-gray-700 text-sm text-gray-400 hover:text-white hover:border-gray-500 transition-colors'
                  >
                    {t('orchestrator.back')}
                  </button>
                )}
                <div className='flex-1' />
                {step !== 'review'
                  ? (
                    <button
                      onClick={() => setStep(STEPS[stepIdx + 1])}
                      disabled={!canNext}
                      className='px-6 py-2.5 rounded-lg bg-blue-600 text-sm text-white font-medium hover:bg-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
                    >
                      {t('orchestrator.next')}
                    </button>
                  )
                  : (
                    <button
                      onClick={() => startDeploy()}
                      disabled={!name}
                      className='flex items-center gap-2 px-6 py-2.5 rounded-lg bg-green-600 hover:bg-green-500 disabled:opacity-40 text-sm font-bold text-white transition-colors shadow-lg shadow-green-900/30'
                    >
                      <Rocket size={14} /> {t('orchestrator.deployProject')}
                    </button>
                  )}
              </>
            )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function OrchestratorPage() {
  const { t } = useTranslation();
  const isPhone = useIsPhoneLayout();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [params] = useSearchParams();
  /** Filtr / cíl nodu — jednotný chrome s ostatními předělanými moduly */
  const [scopeNode, setScopeNode] = useState<string | null>(() => params.get('node'));
  // Deep-link z CodAble handoff: /modules/orchestrator?env=<name> otevře
  // rovnou to prostředí (přesun/správa = orchestrátor; CodAble jen volá UI).
  const envParam = params.get('env');
  const nodeParam = params.get('node');
  useEffect(() => {
    if (nodeParam) setScopeNode(nodeParam);
    if (envParam) setSelected(envParam);
  }, [envParam, nodeParam]);

  if (isPhone && !envParam) {
    return <Navigate to='/phone/orchestrator' replace />;
  }
  // phone + ?env= → plná správa v desktop layoutu uvnitř phone shellu zůstává
  // dostupná přes „Plný orchestrator“; redirect jen pro holý seznam.

  const localList = useQuery({
    queryKey: ['orch-list', 'local'],
    queryFn: () =>
      mcpCall<{
        ok: boolean;
        environments: OrchestratorEnv[];
        local_node?: string;
      }>('orchestrator.orch_list'),
    refetchInterval: 30_000,
  });
  const remoteList = useQuery({
    queryKey: ['orch-list', scopeNode ?? ''],
    queryFn: () =>
      callWidgetTool<{
        ok: boolean;
        environments: OrchestratorEnv[];
        local_node?: string;
      }>('orchestrator.orch_list', {}, { node: scopeNode, identity: null }),
    enabled: !!scopeNode,
    refetchInterval: 30_000,
  });
  const data = scopeNode ? remoteList.data : localList.data;
  const isLoading = scopeNode ? remoteList.isLoading : localList.isLoading;
  const isFetching = scopeNode ? remoteList.isFetching : localList.isFetching;
  const isError = scopeNode ? remoteList.isError : localList.isError;
  const listError = scopeNode ? remoteList.error : localList.error;
  const refetch = scopeNode ? remoteList.refetch : localList.refetch;

  const allEnvs = data?.environments ?? [];
  const localNodeName = data?.local_node ?? localList.data?.local_node ?? 'local';
  const { data: meshPeers } = useMeshPeers();
  const peers = meshPeers ?? [];
  // Lokál i vzdálený nod: jen prostředí TOHO nodu, ne celý mesh katalog.
  const envs = allEnvs.filter((e) => envIsLocalToListedNode(e, localNodeName));
  useEffect(() => {
    if (!selected || isLoading) return;
    if (envs.some((e) => e.name === selected)) return;
    const hidden = (localList.data?.environments ?? []).find((e) => e.name === selected);
    if (hidden && envParam === selected && hidden.is_local === false) {
      const wire = routingKeyForEnv(hidden, peers);
      if (wire && wire !== scopeNode) setScopeNode(wire);
      return;
    }
    setSelected(null);
  }, [selected, envs, envParam, localList.data, peers, scopeNode, isLoading]);

  // Živý stav per-env, jak postupně dorazí z jednotlivých EnvCard dotazů (viz
  // EnvCard.onLiveStatus). Dokud pro env nic nemáme, bereme počáteční (rychlý,
  // ale nemusí být čerstvý) status ze seznamu — StatsGrid tak doplňuje čísla
  // postupně místo čekání na to, až se ověří úplně všechna prostředí najednou
  // (Filip 2026-07-27, 2. kolo: "člověk nechce koukat 2 minuty na kolečko").
  const [liveByEnv, setLiveByEnv] = useState<Record<string, EnvStatus>>({});
  const handleLiveStatus = useCallback((name: string, status: EnvStatus) => {
    setLiveByEnv((prev) => prev[name] === status ? prev : { ...prev, [name]: status });
  }, []);

  // EnvStatus backend type (orch-utils.ts) je 'pending' | 'applied' | 'partial' |
  // 'error' | 'destroying' | 'destroyed'. \"applied\" je úspěšně nasazené
  // prostředí — service-level statusy mohou být running, ale env-level status
  // zůstává applied.
  const classify = (s: string): 'running' | 'error' | 'stopped' => {
    if (s === 'applied' || s === 'running') return 'running';
    if (s === 'partial' || s === 'error') return 'error';
    return 'stopped'; // pending / destroying / destroyed — vypnuto, ne rozbito
  };
  // Všechny karty teď dělají live probe (remote s target_node). Dokud
  // liveByEnv nemá status, nepočítat do Running/Chyba/Zastaveno.
  const checking = envs.filter((e) => !liveByEnv[e.name]).length;
  const running =
    envs.filter((e) => liveByEnv[e.name] && classify(liveByEnv[e.name]) === 'running').length;
  const errored =
    envs.filter((e) => liveByEnv[e.name] && classify(liveByEnv[e.name]) === 'error').length;
  const stopped =
    envs.filter((e) => liveByEnv[e.name] && classify(liveByEnv[e.name]) === 'stopped').length;

  type NodeGroup = { key: string; label: string; isLocal: boolean; envs: OrchestratorEnv[] };
  const scopePeer = scopeNode
    ? peers.find((p) => peerMatchesKey(p, scopeNode))
    : peers.find((p) => p.is_local);
  const nodeGroups: NodeGroup[] = (() => {
    const map = new Map<string, NodeGroup>();
    const scopeLabel = scopePeer
      ? nodeDisplayLabel(scopePeer)
      : (scopeNode ? localNodeName : `${localNodeName} (${t('orchestrator.localShort', 'local')})`);
    for (const e of envs) {
      const isLocalToUi = !scopeNode;
      const key = scopeNode || 'local';
      const label = isLocalToUi
        ? `${scopeLabel} (${t('orchestrator.localShort', 'local')})`
        : scopeLabel;
      if (!map.has(key)) {
        map.set(key, { key, label, isLocal: isLocalToUi, envs: [] });
      }
      map.get(key)!.envs.push(e);
    }
    return [...map.values()];
  })();

  return (
    <div className='space-y-6 p-1 min-w-0'>
      <PageHeader
        title={t('orchestrator.pageTitle')}
        description={t('orchestrator.pageDescription')}
        icon={GitMerge}
        actions={
          <>
            {
              /* Background refetchInterval poll indikátor — isLoading pokrývá jen
                první fetch, takže periodický refresh na pozadí (30s) byl jinak
                neviditelný (Filip 2026-07-27: "při načítání neukazuje průběh"). */
            }
            {!isLoading && isFetching && (
              <span
                className='flex items-center gap-1.5 text-[11px] text-gray-500'
                title={t('orchestrator.refreshing', 'Aktualizuji…')}
              >
                <RefreshCw size={12} className='animate-spin' />
              </span>
            )}
            {!scopeNode && (
              <Button
                variant='primary'
                icon={<Plus size={15} />}
                onClick={() => setShowCreate(true)}
              >
                {t('orchestrator.newEnv')}
              </Button>
            )}
          </>
        }
      />

      <ModuleScopeBar
        node={scopeNode}
        onNodeChange={(node) => {
          setScopeNode(node);
          setSelected(null);
        }}
        onRefresh={async () => {
          await refetch();
        }}
        refreshing={isFetching}
        sticky
      />

      <OrchNodeCtx.Provider value={scopeNode}>
        {isLoading && (
          <div className='py-12 text-center text-gray-500'>
            <Loader2 size={16} className='animate-spin inline mr-2' />
            {t('orchestrator.loading')}
          </div>
        )}

        {isError && !isLoading && (
          <Card>
            <CardContent className='py-16'>
              <EmptyState
                icon={<AlertCircle size={40} />}
                title={t('orchestrator.listLoadFailed', 'Seznam prostředí se nepodařilo načíst.')}
                description={(listError as Error | undefined)?.message}
              />
            </CardContent>
          </Card>
        )}

        {!isLoading && !isError && envs.length === 0 && (
          <Card>
            <CardContent className='py-16'>
              <EmptyState
                icon={<GitMerge size={40} />}
                title={scopeNode
                  ? t('orchestrator.noEnvironmentsOnNode', 'Na tomto nodu nejsou lokální prostředí')
                  : t('orchestrator.noEnvironments')}
                description={scopeNode
                  ? t(
                    'orchestrator.noEnvironmentsOnNodeDesc',
                    'Orchestrátor ukazuje jen projekty tohoto nodu, ne všechno z mesh katalogu.',
                  )
                  : t('orchestrator.noEnvironmentsDesc')}
                action={!scopeNode
                  ? (
                    <Button
                      variant='primary'
                      icon={<Plus size={15} />}
                      onClick={() => setShowCreate(true)}
                    >
                      {t('orchestrator.createFirstEnv')}
                    </Button>
                  )
                  : undefined}
              />
            </CardContent>
          </Card>
        )}

        {envs.length > 0 && (
          <>
            {checking > 0 && (
              <div className='flex items-center gap-2 text-xs text-gray-500 -mb-1'>
                <span className='inline-block w-3 h-3 border-2 border-gray-600 border-t-blue-400 rounded-full animate-spin' />
                {t('orchestrator.verifyingCount', 'Ověřuji {{checking}} z {{total}} prostředí…', {
                  checking,
                  total: envs.length,
                })}
              </div>
            )}
            {
              /* Running/Chyba/Zastaveno se NEDOPLŇUJÍ postupně, jak jednotlivé karty
              dojíždí - dokud check neproběhl u VŠECH prostředí, nevíme jistě
              rozdělení, a ukazovat mezitím "skákající" čísla (natožpak error!)
              vypadá jako reálný problém, který není (Filip 2026-07-27, 3. kolo).
              Dokud checking > 0, tyhle 3 karty ukazují jen "—". Environments
              (celkový počet) je bezpečné ukázat hned - je to čistě z DB, ne
              z live probe. */
            }
            <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
              {[
                {
                  label: t('orchestrator.statEnvironments'),
                  value: envs.length,
                  Icon: Layout,
                  color: 'text-gray-300',
                  accent: 'bg-gray-500/10 text-gray-400',
                },
                {
                  label: t('orchestrator.statRunning'),
                  value: checking > 0 ? null : running,
                  Icon: CheckCircle2,
                  color: 'text-emerald-400',
                  accent: 'bg-emerald-500/10 text-emerald-400',
                },
                {
                  label: t('orchestrator.statError'),
                  value: checking > 0 ? null : errored,
                  Icon: AlertCircle,
                  color: 'text-red-400',
                  accent: 'bg-red-500/10 text-red-400',
                },
                {
                  label: t('orchestrator.statStopped'),
                  value: checking > 0 ? null : stopped,
                  Icon: Clock,
                  color: 'text-gray-400',
                  accent: 'bg-gray-500/10 text-gray-400',
                },
              ].map(({ label, value, Icon, color, accent }) => (
                <Card key={label}>
                  <CardContent className='p-3 flex items-center gap-3'>
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${accent}`}
                    >
                      <Icon size={16} />
                    </div>
                    <div className='min-w-0'>
                      <div className={`text-2xl font-bold leading-none ${color}`}>
                        {value ?? '—'}
                      </div>
                      <div className='text-[10px] text-gray-500 uppercase tracking-wide mt-0.5'>
                        {label}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className='grid grid-cols-1 xl:grid-cols-3 gap-4'>
              <div
                className={`xl:col-span-1 space-y-4 ${selected ? 'hidden xl:block' : 'block'}`}
              >
                {nodeGroups.map((group) => (
                  <div key={group.key} className='space-y-2'>
                    <div
                      className={`flex items-center gap-2 px-1 sticky top-0 z-10 py-1 backdrop-blur-sm ${
                        group.isLocal ? 'text-emerald-400' : 'text-violet-300'
                      }`}
                    >
                      <Server size={12} className='shrink-0' />
                      <span className='text-[11px] font-bold uppercase tracking-wide truncate'>
                        {group.label}
                      </span>
                      <span className='text-[10px] text-gray-600 tabular-nums'>
                        {group.envs.length}
                      </span>
                      <div className='flex-1 h-px bg-gray-800/80' />
                      {group.isLocal
                        ? (
                          <span className='text-[9px] font-bold uppercase text-emerald-400/80 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded'>
                            {t('orchestrator.localShort', 'local')}
                          </span>
                        )
                        : (
                          <span className='text-[9px] font-bold uppercase text-violet-300/90 bg-violet-500/10 border border-violet-500/25 px-1.5 py-0.5 rounded'>
                            {t('orchestrator.remoteNode', 'remote')}
                          </span>
                        )}
                    </div>
                    <div className='space-y-2'>
                      {group.envs.map((env) => (
                        <EnvCard
                          key={env.name}
                          env={env}
                          selected={selected === env.name}
                          onSelect={() => setSelected(env.name === selected ? null : env.name)}
                          onLiveStatus={handleLiveStatus}
                          showNodeBadge={false}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div
                className={`xl:col-span-2 ${selected ? 'block' : 'hidden xl:block'}`}
              >
                {selected
                  ? (
                    <div className='space-y-3'>
                      <button
                        type='button'
                        onClick={() => setSelected(null)}
                        className='xl:hidden flex items-center gap-1.5 text-sm text-gray-400 hover:text-white py-1.5 px-1 touch-manipulation'
                      >
                        <span aria-hidden>←</span>
                        {t('orchestrator.backToList', 'Zpět na seznam')}
                      </button>
                      <EnvDetail
                        envId={selected}
                        primaryNode={envs.find((e) => e.name === selected)?.primary_node ??
                          envs.find((e) => e.name === selected)?.target_node}
                        isLocal={envs.find((e) => e.name === selected)?.is_local}
                        onDeleted={() => {
                          setSelected(null);
                          qc.invalidateQueries({ queryKey: ['orch-list'] });
                        }}
                      />
                    </div>
                  )
                  : (
                    <Card>
                      <CardContent className='py-20'>
                        <EmptyState
                          icon={<Zap size={32} />}
                          title={t('orchestrator.selectEnvLeft')}
                        />
                      </CardContent>
                    </Card>
                  )}
              </div>
            </div>
          </>
        )}

        {showCreate && (
          <CreateEnvForm
            onClose={() => setShowCreate(false)}
            onCreated={() => {
              setShowCreate(false);
              qc.invalidateQueries({ queryKey: ['orch-list'] });
            }}
          />
        )}
      </OrchNodeCtx.Provider>
    </div>
  );
}
